import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal
} from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { PaymentIframeModalComponent } from 'src/app/shared/components/payment-iframe-modal/payment-iframe-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import {
  PaymentOrderType,
  buildPaymentResultUrl,
  formatCountdown,
  getPaymentRemainingMs,
  resolvePaymentExpireTime,
  storePaymentSessionSnapshot
} from 'src/app/shared/utils/payment-flow.util';
import { ConstantUtil } from 'src/app/util/constant.util';
import { IdUtil } from 'src/app/util/id.util';

import { AssistantInstancesViewComponent } from './views/assistant-instances-view.component';
import { AssistantMarketViewComponent } from './views/assistant-market-view.component';
import { AssistantMineViewComponent } from './views/assistant-mine-view.component';

type AssistantSource = 'Custom' | 'Subscription';
type AssistantStatus = 'Enable' | 'Disable';
type AssistantResponseMode = 'Qa' | 'CollectOnly' | 'Batch';
type ReplyVisibility = 'Employee' | 'Customer' | 'Both';
type AssistantPaymentStatus = 'Unpaid' | 'Paid';
type AssistantStoreInstanceFilter = 'All' | 'Trialing' | 'Active' | 'Expiring' | 'Expired' | 'Disabled' | 'Purged';
type CreateFormControl =
  | 'avatar'
  | 'name'
  | 'description'
  | 'responseMode'
  | 'webhookUrl'
  | 'batchMessageCount'
  | 'batchIntervalMinutes'
  | 'batchKeywords'
  | 'messageTypes'
  | 'senderTypes'
  | 'replyReceiverTypes';
type BalanceType = 'uupon';
type AssistantStoreView = 'mine' | 'instances' | 'market';
const TRIAL_PACKAGE_ID = '__trial__';
const ASSISTANT_PRICING_CODE = 'ASSISTANT_LIMIT';
const STORE_RENEWAL_REFRESH_RETRY_DELAY_MS = 1000;
const STORE_RENEWAL_REFRESH_MAX_ATTEMPTS = 3;

interface SelectedPricing {
  pricingConfigId: string;
  pricingItemId: string;
  pricingCode: string;
}

interface AssistantItem {
  id: string;
  orderId?: string;
  paymentUrl?: string;
  name: string;
  source: AssistantSource;
  status: AssistantStatus;
  paymentStatus?: AssistantPaymentStatus | string;
  needPay?: boolean;
  trial?: boolean;
  expireTime?: number;
  responseMode: AssistantResponseMode;
  description: string;
  webhookUrl: string;
  providerName?: string;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  avatarUrl?: string;
  reserveTimeoutSeconds?: number;
  payStartTime?: number;
  payExpireTime?: number;
  latestOrderStatus?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionValidUntil?: number;
  canRenew?: boolean;
}

interface StepMeta {
  titleKey: string;
  icon: string;
}

interface OptionItem {
  value: string;
  labelKey: string;
  icon: string;
}

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  trial?: boolean;
  trialDays?: number;
  recommended?: boolean;
  order?: number;
  durationMonths?: number;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
}

interface BalanceCard {
  type: BalanceType;
  points: number;
  deductRate: number;
}

interface SettlementDetailRow {
  label: string;
  value: string;
}

interface AssistantPackageItem {
  id?: string;
  pricingConfigId?: string;
  pricingCode?: string;
  durationMonths?: number;
  trialEnabled?: boolean;
  trialDays?: number;
  priceCny?: number;
  discountPriceCny?: number;
  discountRate?: number;
  description?: string;
}

interface AssistantListPayload {
  pageIndex: number;
  pageSize: number;
  direction: 'asc' | 'desc';
  orderBy: string;
  key?: string;
  refreshTime: number;
  source?: AssistantSource;
  status?: AssistantStatus;
  pricingCode?: string;
}

interface AssistantListData {
  trialUsed?: boolean | null;
  totalCount?: number;
  count?: number;
  hasNextPage?: boolean;
  pageIndex?: number;
  refreshTime?: number;
  items?: AssistantListItem[];
}

interface AssistantListItem {
  id?: string;
  tenantId?: string;
  orderId?: string;
  latestOrderId?: string;
  paymentUrl?: string;
  lastOrder?: AssistantOrderData;
  name?: string;
  source?: string;
  status?: string;
  paymentStatus?: AssistantPaymentStatus | string;
  needPay?: boolean;
  trial?: boolean | string | number | null;
  expireTime?: number;
  responseMode?: AssistantResponseMode;
  description?: string;
  webhookUrl?: string;
  providerName?: string;
  avatarUrl?: string;
  avatar?: string;
  avatarFileUrl?: string;
  iconUrl?: string;
  imageUrl?: string;
  headImg?: string;
  headImage?: string;
  updatedAt?: number;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  reserveTimeoutSeconds?: number;
  payStartTime?: number;
  payExpireTime?: number;
  currentPricingConfigId?: string;
  currentPricingItemId?: string;
  currentPricingCode?: string;
  latestPricingConfigId?: string;
  latestPricingItemId?: string;
  latestPricingCode?: string;
  latestOrderStatus?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionValidUntil?: number;
  canRenew?: boolean;
}

interface AssistantCreatePayload {
  tenantId?: string;
  aipoolTenantId?: string;
  assistantId?: string;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  source: 'Custom';
  name: string;
  avatarUrl?: string;
  description: string;
  responseMode: 'Qa' | 'CollectOnly' | 'Batch';
  webhookUrl: string;
  messageTypes: string[];
  senderTypes: string[];
  replyVisibleFor?: ReplyVisibility;
  replyVisibleScope?: ReplyVisibility;
  replyVisibleTargets?: string[];
  replyReceiverTypes?: string[];
  historyDays: number;
  deductAilePoints?: number;
  deductUUPonPoints?: number;
  payableCash?: number;
  useTrial?: boolean;
  'batchTrigger.triggerMessageCount'?: number;
  'batchTrigger.triggerIntervalSeconds'?: number;
  'batchTrigger.triggerKeywords'?: string[];
}

interface AssistantOrderData {
  id?: string;
  orderId?: string;
  aipoolOrderId?: string;
  ddpayOrderId?: string;
  paymentUrl?: string;
  status?: AssistantStatus;
  orderStatus?: number;
  pointsStatus?: number;
  paymentStatus?: number;
  payRequired?: boolean;
  pricingConfigId?: string;
  pricingItemId?: string;
  pricingCode?: string;
  durationDays?: number;
  itemSnapshotJson?: string;
}

interface AssistantStoreDurationPackage {
  packageId?: string;
  name?: string;
  durationDays: number;
  priceCny: number;
  recommended?: boolean;
}

interface AssistantStoreCardFact {
  icon: 'robot' | 'message' | 'calendar';
  label: string;
}

interface AssistantStoreCardFactsCacheEntry {
  language: string;
  facts: AssistantStoreCardFact[];
}

interface AssistantStoreApp {
  id?: string;
  templateId?: string;
  name: string;
  providerName?: string;
  shortDescription?: string;
  description?: string;
  iconUrl?: string;
  category?: string;
  customCategory?: string;
  version?: string;
  featureTags?: string[];
  durationPackages?: AssistantStoreDurationPackage[];
  responseMode?: AssistantResponseMode;
  messageTypes?: string[];
  senderTypes?: string[];
  replyVisibleFor?: ReplyVisibility;
  replyVisibleScope?: ReplyVisibility;
  replyVisibleTargets?: string[];
  replyReceiverTypes?: string[];
  historyDays?: number;
  batchTrigger?: {
    triggerMessageCount?: number;
    triggerIntervalSeconds?: number;
    triggerKeywords?: string[];
  };
}

interface AssistantStoreSubscription {
  subscriptionId: string;
  assistantId: string;
  templateId: string;
  name?: string;
  description?: string;
  avatarUrl?: string;
  templateName?: string;
  providerName?: string;
  serviceNumberName?: string;
  triggerSummary?: string;
  configSummary?: string;
  templateVersion?: string;
  status: 'PendingPayment' | 'Trialing' | 'Active' | 'Expired' | 'Disabled' | 'Purged';
  createMethod?: 'Trial' | 'Purchase' | 'CustomCreate' | 'Official';
  validFrom?: number;
  validUntil?: number;
  dataRetentionUntil?: number;
  latestPackageId?: string;
  latestPackageName?: string;
  latestPackageDurationDays?: number;
  latestOrderId?: string;
  latestOrderStatus?: string;
  paymentUrl?: string;
  reserveTimeoutSeconds?: number;
  payStartTime?: number;
  payExpireTime?: number;
  canRenew?: boolean;
  editable?: boolean;
}

interface AssistantStoreRenewalContext {
  assistantId: string;
  previous: AssistantStoreSubscription;
}

interface AssistantStoreListData<T> {
  totalCount?: number;
  items?: T[];
}

type AssistantWizardMode = 'create' | 'edit' | 'pay' | 'renew';

interface AssistantDetailData extends AssistantListItem {
  messageTypes?: string[];
  senderTypes?: string[];
  replyVisibleFor?: ReplyVisibility;
  replyVisibleScope?: ReplyVisibility;
  replyVisibleTargets?: string[];
  replyReceiverTypes?: string[];
  historyDays?: number;
  currentPricingDurationDays?: number;
  deductAilePoints?: number | string | null;
  deductUUPonPoints?: number | string | null;
  usedAilePoints?: number | string | null;
  usedUUPonPoints?: number | string | null;
  ailePoints?: number | string | null;
  uuponPoints?: number | string | null;
  batchTrigger?: {
    triggerMessageCount?: number;
    triggerIntervalSeconds?: number;
    triggerKeywords?: string[];
  };
}

@Component({
  selector: 'personal-assistant-management',
  standalone: true,
  imports: [
    CommonModule,
    I18nPipe,
    TestIdDirective,
    AssistantMineViewComponent,
    AssistantInstancesViewComponent,
    AssistantMarketViewComponent
  ],
  templateUrl: './personal-assistant-management.component.html',
  styleUrls: ['./personal-assistant-management.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class PersonalAssistantManagementComponent implements OnInit, OnDestroy {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpService);
  private readonly route = inject(ActivatedRoute);
  private readonly modal = inject(NzModalService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(private readonly message: NzMessageService) {}

  readonly viewModel = this;
  readonly mobileSearchExpanded = signal(false);
  readonly assistantStoreView = signal<AssistantStoreView>('mine');
  readonly marketKeyword = signal('');
  readonly marketCategory = signal('All');
  readonly storeInstanceKeyword = signal('');
  readonly storeInstanceStatusFilter = signal<AssistantStoreInstanceFilter>('All');
  readonly storeLoading = signal(false);
  readonly storeActionLoadingId = signal<string | null>(null);
  readonly marketApps = signal<AssistantStoreApp[]>([]);
  readonly storeSubscriptions = signal<AssistantStoreSubscription[]>([]);
  readonly storeDetailVisible = signal(false);
  readonly storeDetailLoading = signal(false);
  readonly storeInstanceDetailVisible = signal(false);
  readonly storeInstanceDetailLoading = signal(false);
  readonly selectedStoreInstance = signal<AssistantStoreSubscription | null>(null);
  readonly storeInstanceDetail = signal<AssistantDetailData | null>(null);
  readonly storePackageModalVisible = signal(false);
  readonly selectedStoreApp = signal<AssistantStoreApp | null>(null);
  readonly selectedStorePackageId = signal<string | null>(null);
  readonly selectedStorePurchaseBizId = signal<string | null>(null);
  readonly keyword = signal('');
  readonly selectedSource = signal<'All' | AssistantSource>('All');
  readonly selectedStatus = signal<'All' | AssistantStatus>('All');
  readonly loading = signal(false);
  readonly submitLoading = signal(false);
  readonly payingAssistantId = signal<string | null>(null);
  createModalVisible = false;
  editingAssistantId: string | null = null;
  wizardMode: AssistantWizardMode = 'create';
  createStep = 0;
  avatarPreview = '';
  batchTriggerConditionMissing = false;
  private avatarObjectUrl: string | null = null;
  private storeInstanceAvatarObjectUrl: string | null = null;
  private storeInstanceAvatarFile: File | null = null;
  private storeInstanceAvatarChanged = false;
  private avatarFile: File | null = null;
  private avatarChanged = false;
  private editingAvatarUrl = '';
  storeInstanceName = '';
  storeInstanceAvatarPreview = '';
  historyDays = 7;
  selectedPricingItemId = '';
  private aipoolTenantId = '';
  packagesLoading = false;
  trialUsed = false;
  trialStateKnown = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  loadingMore = false;
  hasMore = true;
  direction: 'asc' | 'desc' = 'asc';
  orderBy = 'id';
  refreshTime = Date.now();
  now = Date.now();
  private keywordDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: number | null = null;
  private storeRenewalRefreshTimer: number | null = null;
  private pendingPreferredPricing: Partial<SelectedPricing> | null = null;
  private pendingDeductionInputs: Record<BalanceType, number | null> | null = null;
  private payingOrderId: string | null = null;
  private trialIdempotencyKeys?: Map<string, string>;
  private readonly storeCardFactsCache = new WeakMap<AssistantStoreApp, AssistantStoreCardFactsCacheEntry>();
  deductionInputs: Record<BalanceType, number | null> = {
    uupon: null
  };

  balanceCards: BalanceCard[] = this.buildBalanceCards();

  readonly createSteps: StepMeta[] = [
    { titleKey: 'assistant.management.create.step.basic', icon: 'star' },
    { titleKey: 'assistant.management.create.step.response', icon: 'bell' },
    { titleKey: 'assistant.management.create.step.filter', icon: 'filter' },
    { titleKey: 'assistant.management.create.step.pay', icon: 'credit-card' }
  ];

  readonly responseModeCards: Array<{
    value: AssistantResponseMode;
    titleKey: string;
    descKey: string;
    usageKey: string;
    icon: string;
  }> = [
    {
      value: 'Qa',
      titleKey: 'assistant.management.create.mode.qa.title',
      descKey: 'assistant.management.create.mode.qa.desc',
      usageKey: 'assistant.management.create.mode.qa.usage',
      icon: 'message'
    },
    {
      value: 'CollectOnly',
      titleKey: 'assistant.management.create.mode.collect.title',
      descKey: 'assistant.management.create.mode.collect.desc',
      usageKey: 'assistant.management.create.mode.collect.usage',
      icon: 'inbox'
    },
    {
      value: 'Batch',
      titleKey: 'assistant.management.create.mode.batch.title',
      descKey: 'assistant.management.create.mode.batch.desc',
      usageKey: 'assistant.management.create.mode.batch.usage',
      icon: 'database'
    }
  ];

  readonly messageTypeOptions: OptionItem[] = [
    { value: 'Text', labelKey: 'assistant.management.create.message-type.text', icon: 'file-text' },
    { value: 'Image', labelKey: 'assistant.management.create.message-type.image', icon: 'file-image' },
    { value: 'File', labelKey: 'assistant.management.create.message-type.file', icon: 'file' },
    { value: 'Voice', labelKey: 'assistant.management.create.message-type.voice', icon: 'audio' },
    { value: 'Video', labelKey: 'assistant.management.create.message-type.video', icon: 'video-camera' }
  ];

  readonly senderTypeOptions: OptionItem[] = [
    { value: 'Employee', labelKey: 'assistant.management.create.sender-type.employee', icon: 'user' },
    { value: 'Customer', labelKey: 'assistant.management.create.sender-type.customer', icon: 'team' }
    // { value: 'Member', labelKey: 'assistant.management.create.sender-type.member', icon: 'usergroup-add' }
  ];

  readonly pricingPlans = signal<PricingPlan[]>(this.getFallbackPricingPlans());

  readonly createForm = this.fb.group({
    avatar: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(50)]],
    description: ['', [Validators.required, Validators.maxLength(200)]],
    responseMode: ['Qa', [Validators.required]],
    webhookUrl: ['', [Validators.required, Validators.pattern(/^https:\/\/.+/i)]],
    batchMessageCount: [10, [this.optionalMin(1)]],
    batchIntervalMinutes: [60, [this.optionalMin(1)]],
    batchKeywords: [''],
    messageTypes: [['Text'], [this.minArrayLength(1)]],
    senderTypes: [['Customer'], [this.minArrayLength(1)]],
    replyReceiverTypes: [[] as string[]]
  });

  readonly sourceOptions: Array<{ labelKey: string; value: 'All' | AssistantSource }> = [
    { labelKey: 'assistant.management.filter.source.all', value: 'All' },
    { labelKey: 'assistant.management.source.custom', value: 'Custom' },
    { labelKey: 'assistant.management.source.subscription', value: 'Subscription' }
  ];

  readonly statusOptions: Array<{ labelKey: string; value: 'All' | AssistantStatus }> = [
    { labelKey: 'assistant.management.filter.status.all', value: 'All' },
    { labelKey: 'assistant.management.status.enable', value: 'Enable' },
    { labelKey: 'assistant.management.status.disable', value: 'Disable' }
  ];
  readonly storeInstanceStatusOptions: Array<{ labelKey: string; value: AssistantStoreInstanceFilter }> = [
    { labelKey: 'assistant.management.store.filter.status.all', value: 'All' },
    { labelKey: 'assistant.management.store.status.trialing', value: 'Trialing' },
    { labelKey: 'assistant.management.store.status.active', value: 'Active' },
    { labelKey: 'assistant.management.store.status.expiring', value: 'Expiring' },
    { labelKey: 'assistant.management.store.status.expired', value: 'Expired' },
    { labelKey: 'assistant.management.store.status.disabled', value: 'Disabled' },
    { labelKey: 'assistant.management.store.status.purged', value: 'Purged' }
  ];

  readonly assistants = signal<AssistantItem[]>([]);

  readonly filteredAssistants = computed(() => this.assistants());
  readonly marketCategories = computed(() => {
    const categories = this.marketApps()
      .map(item => item.customCategory || item.category)
      .filter((item): item is string => !!item);
    return ['All', ...Array.from(new Set(categories))];
  });
  readonly filteredMarketApps = computed(() => {
    const keyword = this.marketKeyword().trim().toLowerCase();
    const category = this.marketCategory();
    return this.marketApps().filter(item => {
      const appCategory = item.customCategory || item.category || '';
      const matchedCategory = category === 'All' || category === appCategory;
      const searchable =
        `${item.name} ${item.shortDescription ?? ''} ${item.description ?? ''} ${(item.featureTags ?? []).join(' ')}`.toLowerCase();
      return matchedCategory && (!keyword || searchable.includes(keyword));
    });
  });
  readonly storeInstanceStats = computed(() => {
    const items = this.storeSubscriptions();
    return {
      total: items.length,
      trialing: items.filter(item => item.status === 'Trialing').length,
      active: items.filter(item => item.status === 'Active' && !this.isExpiringStoreSubscription(item)).length,
      expiring: items.filter(item => this.isExpiringStoreSubscription(item)).length,
      expired: items.filter(item => item.status === 'Expired').length,
      disabled: items.filter(item => item.status === 'Disabled').length,
      purged: items.filter(item => item.status === 'Purged').length,
      recoverable: items.filter(item => this.canRecoverStoreSubscription(item)).length
    };
  });
  readonly filteredStoreSubscriptions = computed(() => {
    const keyword = this.storeInstanceKeyword().trim().toLowerCase();
    const status = this.storeInstanceStatusFilter();
    return this.storeSubscriptions().filter(item => {
      const matchesStatus =
        status === 'All' ||
        (status === 'Expiring'
          ? this.isExpiringStoreSubscription(item)
          : item.status === status && !this.isHiddenByStatusGroup(item, status));
      const searchable = [
        item.name,
        item.templateName,
        item.templateId,
        item.serviceNumberName,
        item.triggerSummary,
        item.configSummary,
        item.providerName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return matchesStatus && (!keyword || searchable.includes(keyword));
    });
  });

  ngOnInit(): void {
    this.loadBalanceSummary();
    this.loadAssistantList();
    this.loadAssistantStoreCatalog();
    this.loadAssistantStoreInstances();
    this.startCountdown();
  }

  selectAssistantStoreView(view: AssistantStoreView): void {
    this.assistantStoreView.set(view);
    if (view === 'mine') {
      this.refreshAssistantList();
      return;
    }
    if (view === 'market') {
      this.loadAssistantStoreCatalog();
      return;
    }
    this.loadAssistantStoreInstances();
  }

  shouldShowSelfBuiltActions(): boolean {
    return this.assistantStoreView() === 'mine';
  }

  refreshCurrentAssistantView(): void {
    const view = this.assistantStoreView();
    if (view === 'mine') {
      this.refreshAssistantList();
      return;
    }
    if (view === 'market') {
      this.loadAssistantStoreCatalog();
      return;
    }
    this.loadAssistantStoreInstances();
  }

  updateMarketKeyword(value: string): void {
    this.marketKeyword.set(value);
  }

  updateMarketCategory(value: string): void {
    this.marketCategory.set(value);
  }

  updateStoreInstanceKeyword(value: string): void {
    this.storeInstanceKeyword.set(value);
  }

  updateStoreInstanceStatusFilter(value: AssistantStoreInstanceFilter): void {
    this.storeInstanceStatusFilter.set(value);
  }

  refreshAssistantStoreCatalog(): void {
    this.loadAssistantStoreCatalog();
    this.loadAssistantStoreInstances();
  }

  refreshAssistantStoreInstances(): void {
    this.loadAssistantStoreInstances();
  }

  shouldShowStoreTrial(app: AssistantStoreApp): boolean {
    return !this.findStoreSubscription(this.getStoreTemplateId(app));
  }

  openExistingStoreInstance(subscription?: AssistantStoreSubscription): void {
    if (!subscription) return;
    this.closeAssistantStoreDetail();
    this.openStoreInstanceDetail(subscription);
  }

  openAssistantStoreDetail(app: AssistantStoreApp): void {
    const templateId = this.getStoreTemplateId(app);
    if (!templateId) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.storeDetailVisible.set(true);
    this.selectedStoreApp.set(app);
    this.selectDefaultStorePackage(app);
    this.storeDetailLoading.set(true);

    const url = ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_ITEM.replace('{templateId}', encodeURIComponent(templateId));
    this.http
      .post(url, {})
      .pipe(finalize(() => this.storeDetailLoading.set(false)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const detail = (res?.data?.item ?? res?.data ?? {}) as Partial<AssistantStoreApp>;
          const merged = {
            ...app,
            ...detail,
            durationPackages: detail.durationPackages ?? app.durationPackages,
            featureTags: detail.featureTags ?? app.featureTags
          } as AssistantStoreApp;
          this.selectedStoreApp.set(merged);
          this.selectDefaultStorePackage(merged);
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  openStoreInstanceDetail(subscription: AssistantStoreSubscription): void {
    const assistantId = String(subscription.assistantId ?? '').trim();
    if (!assistantId) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.revokeStoreInstanceAvatarObjectUrl();
    this.storeInstanceAvatarFile = null;
    this.storeInstanceAvatarChanged = false;
    this.selectedStoreInstance.set(subscription);
    this.storeInstanceDetail.set(null);
    this.storeInstanceName = this.getStoreInstanceName(subscription);
    this.storeInstanceAvatarPreview = subscription.avatarUrl ?? '';
    this.storeInstanceDetailVisible.set(true);
    this.storeInstanceDetailLoading.set(true);

    const api = ConstantUtil.ADMIN_ASSISTANT_V1_ITEM.replace('{assistantId}', encodeURIComponent(assistantId));
    this.http
      .post(api, {})
      .pipe(finalize(() => this.storeInstanceDetailLoading.set(false)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const detail = (res?.data ?? {}) as AssistantDetailData;
          this.storeInstanceDetail.set(detail);
          this.storeInstanceName = String(detail.name ?? subscription.name ?? '').trim();
          this.storeInstanceAvatarPreview = this.resolveAvatarUrl(detail) ?? subscription.avatarUrl ?? '';
          this.cdr.detectChanges();
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  closeStoreInstanceDetail(): void {
    this.storeInstanceDetailVisible.set(false);
    this.storeInstanceDetailLoading.set(false);
    this.selectedStoreInstance.set(null);
    this.storeInstanceDetail.set(null);
    this.storeInstanceName = '';
    this.storeInstanceAvatarPreview = '';
    this.storeInstanceAvatarFile = null;
    this.storeInstanceAvatarChanged = false;
    this.revokeStoreInstanceAvatarObjectUrl();
  }

  onStoreInstanceAvatarFileChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!input || !file) return;
    if (!file.type.startsWith('image/')) {
      this.message.warning(this.t('assistant.management.create.avatar.invalid-type'));
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.message.warning(this.t('assistant.management.create.avatar.invalid-size'));
      input.value = '';
      return;
    }

    this.revokeStoreInstanceAvatarObjectUrl();
    this.storeInstanceAvatarObjectUrl = URL.createObjectURL(file);
    this.storeInstanceAvatarPreview = this.storeInstanceAvatarObjectUrl;
    this.storeInstanceAvatarFile = file;
    this.storeInstanceAvatarChanged = true;
    input.value = '';
  }

  saveStoreInstanceDetail(): void {
    const subscription = this.selectedStoreInstance();
    const detail = this.storeInstanceDetail();
    const assistantId = String(subscription?.assistantId ?? detail?.id ?? '').trim();
    const name = this.storeInstanceName.trim();
    if (!assistantId || !name) {
      this.message.warning(this.t('assistant.management.create.message.complete-required'));
      return;
    }
    if (this.storeInstanceAvatarChanged && !this.storeInstanceAvatarFile) {
      this.message.warning(this.t('assistant.management.create.basic.avatar.required'));
      return;
    }

    const payload = { id: assistantId, name };

    this.storeInstanceDetailLoading.set(true);
    this.http
      .postFormData(
        ConstantUtil.ADMIN_ASSISTANT_V1_PROFILE,
        payload,
        this.storeInstanceAvatarChanged ? this.storeInstanceAvatarFile : null,
        'avatarFile'
      )
      .pipe(finalize(() => this.storeInstanceDetailLoading.set(false)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.save-failed'));
            return;
          }
          this.message.success(this.t('common.save-success'));
          this.closeStoreInstanceDetail();
          this.loadAssistantStoreInstances();
        },
        error: () => this.message.error(this.t('common.save-failed'))
      });
  }

  getStoreInstanceMessageTypesLabel(detail: AssistantDetailData): string {
    return this.getOptionLabels(this.messageTypeOptions, this.fromApiMessageTypes(detail.messageTypes));
  }

  getStoreInstanceSenderTypesLabel(detail: AssistantDetailData): string {
    return this.getOptionLabels(this.senderTypeOptions, this.fromApiSenderTypes(detail.senderTypes));
  }

  getStoreInstanceReplyReceiverTypesLabel(detail: AssistantDetailData): string {
    return this.getOptionLabels(this.senderTypeOptions, this.getReplyReceiverTypesForDisplay(detail));
  }

  getStoreInstanceTriggerKeywords(detail: AssistantDetailData): string {
    return detail.batchTrigger?.triggerKeywords?.join('、') || '-';
  }

  getStoreInstanceTriggerCount(detail: AssistantDetailData): string {
    const count = Number(detail.batchTrigger?.triggerMessageCount ?? 0);
    return count > 0 ? String(count) : '-';
  }

  getStoreInstanceTriggerInterval(detail: AssistantDetailData): string {
    const seconds = Number(detail.batchTrigger?.triggerIntervalSeconds ?? 0);
    if (seconds <= 0) return '-';
    return seconds % 60 === 0 ? `${seconds / 60} min` : `${seconds} sec`;
  }

  isStoreInstanceBatchMode(detail: AssistantDetailData): boolean {
    return this.isStoreBatchMode(detail);
  }

  private getOptionLabels(options: OptionItem[], values: string[]): string {
    const selected = new Set(values);
    const labels = options.filter(option => selected.has(option.value)).map(option => this.t(option.labelKey));
    return labels.join('、') || '-';
  }

  private revokeStoreInstanceAvatarObjectUrl(): void {
    if (!this.storeInstanceAvatarObjectUrl) return;
    URL.revokeObjectURL(this.storeInstanceAvatarObjectUrl);
    this.storeInstanceAvatarObjectUrl = null;
  }

  closeAssistantStoreDetail(): void {
    this.storeDetailVisible.set(false);
    this.storeDetailLoading.set(false);
    this.selectedStoreApp.set(null);
    this.selectedStorePackageId.set(null);
    this.storePackageModalVisible.set(false);
  }

  closeAssistantStorePackageModal(): void {
    this.storePackageModalVisible.set(false);
    this.selectedStorePackageId.set(null);
    this.selectedStorePurchaseBizId.set(null);
    this.deductionInputs = { uupon: null };
  }

  openAssistantStorePackageModal(): void {
    const app = this.selectedStoreApp();
    if (!app || (app.durationPackages || []).length === 0) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.selectedStorePurchaseBizId.set(null);
    this.selectDefaultStorePackage(app);
    this.loadBalanceSummary();
    this.deductionInputs = { uupon: null };
    this.storeDetailVisible.set(false);
    this.storePackageModalVisible.set(true);
  }

  selectStorePackage(packageId?: string): void {
    if (packageId) {
      this.selectedStorePackageId.set(packageId);
    }
  }

  startSelectedAssistantStoreTrial(): void {
    const app = this.selectedStoreApp();
    if (app) {
      this.startAssistantStoreTrial(app);
    }
  }

  startSelectedAssistantStorePurchase(): void {
    const app = this.selectedStoreApp();
    const templateId = app ? this.getStoreTemplateId(app) : '';
    const packageId = this.selectedStorePackageId();
    const durationPackage = app?.durationPackages?.find(item => item.packageId === packageId);
    if (!app || !templateId || !durationPackage?.packageId) {
      return;
    }
    if (!this.validateUUPonMultiple()) {
      return;
    }

    this.storeActionLoadingId.set(`purchase-${durationPackage.packageId}`);
    const bizId = this.selectedStorePurchaseBizId();
    const renewal = this.captureSelectedStoreRenewal();
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_PURCHASE, {
        ...(bizId ? { bizId } : {}),
        aipoolTenantId: this.aipoolTenantId,
        templateId,
        durationPackageId: durationPackage.packageId,
        deductUUPonPoints: Math.max(0, Math.floor(this.getStorePackageDeductPoints('uupon')))
      })
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const order = res?.data ?? {};
          this.handleAssistantStorePurchaseOrder(order, renewal);
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  isStorePackagePaymentLoading(): boolean {
    return this.storeActionLoadingId() !== null || this.payingAssistantId() !== null || this.submitLoading();
  }

  updateKeyword(value: string): void {
    this.keyword.set(value);
    if (this.keywordDebounceTimer) {
      clearTimeout(this.keywordDebounceTimer);
    }
    this.keywordDebounceTimer = setTimeout(() => {
      this.reloadAssistantList(true);
    }, 300);
  }

  updateSource(value: 'All' | AssistantSource): void {
    this.selectedSource.set(value);
    this.reloadAssistantList(true);
  }

  updateStatus(value: 'All' | AssistantStatus): void {
    this.selectedStatus.set(value);
    this.reloadAssistantList(true);
  }

  refreshAssistantList(): void {
    this.reloadAssistantList(true);
  }

  toggleMobileSearch(): void {
    this.mobileSearchExpanded.update(value => !value);
  }

  onListScroll(event: Event): void {
    if (this.loading() || this.loadingMore || !this.hasMore) return;
    const target = event.target as HTMLElement;
    const threshold = 80;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (!reachedBottom) return;
    this.pageIndex += 1;
    this.loadAssistantList(true);
  }

  getStatusLabel(status: AssistantStatus): string {
    return status === 'Enable' ? 'assistant.management.status.enable' : 'assistant.management.status.disable';
  }

  getStatusColor(status: AssistantStatus): string {
    return status === 'Enable' ? 'green' : 'default';
  }

  getSourceLabel(source: AssistantSource): string {
    return source === 'Custom' ? 'assistant.management.source.custom' : 'assistant.management.source.subscription';
  }

  getResponseLabel(mode: AssistantResponseMode): string {
    if (mode === 'Qa') return 'assistant.management.response.qa';
    if (mode === 'CollectOnly') return 'assistant.management.response.collect-only';
    return 'assistant.management.response.batch';
  }

  getAssistantDescription(item: AssistantItem): string {
    return String(item.description ?? '').trim() || '-';
  }

  shouldShowAssistantWebhook(item: AssistantItem): boolean {
    return item.source === 'Custom' && !!String(item.webhookUrl ?? '').trim();
  }

  getAssistantProviderName(item: AssistantItem): string {
    const directProvider = String(item.providerName ?? '').trim();
    if (directProvider) return directProvider;

    const subscription = this.storeSubscriptions().find(candidate => candidate.assistantId === item.id);
    const subscriptionProvider = String(subscription?.providerName ?? '').trim();
    if (subscriptionProvider) return subscriptionProvider;

    const catalogProvider = String(
      this.marketApps().find(candidate => this.getStoreTemplateId(candidate) === subscription?.templateId)?.providerName ?? ''
    ).trim();
    return catalogProvider || this.t('assistant.store.provider.official');
  }

  getAvatarText(item: AssistantItem): string {
    return item.name.slice(0, 1);
  }

  getCurrentStepText(): string {
    return `${this.createStep + 1} / ${this.visibleCreateSteps.length}`;
  }

  getStepState(stepIndex: number): 'done' | 'current' | 'pending' {
    if (stepIndex < this.createStep) return 'done';
    if (stepIndex === this.createStep) return 'current';
    return 'pending';
  }

  get visibleCreateSteps(): StepMeta[] {
    return this.wizardMode === 'edit' ? this.createSteps.slice(0, 3) : this.createSteps;
  }

  get lastCreateStepIndex(): number {
    return this.visibleCreateSteps.length - 1;
  }

  isEditWizard(): boolean {
    return this.wizardMode === 'edit';
  }

  isPayWizard(): boolean {
    return this.wizardMode === 'pay';
  }

  isRenewWizard(): boolean {
    return this.wizardMode === 'renew';
  }

  getSubmitButtonLabelKey(): string {
    return this.isEditWizard() ? 'common.save' : 'assistant.management.create.footer.confirm-pay';
  }

  toggleStatus(item: AssistantItem): void {
    const targetStatus: AssistantStatus = item.status === 'Enable' ? 'Disable' : 'Enable';
    this.http.post(ConstantUtil.ADMIN_ASSISTANT_V1_STATUS, { id: item.id, status: targetStatus }).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          showFallbackHttpError(this.message, res?.msg, this.t('common.update-failed'));
          return;
        }
        this.assistants.update(list =>
          list.map(cur => (cur.id === item.id ? { ...cur, status: cur.status === 'Enable' ? 'Disable' : 'Enable' } : cur))
        );
        this.message.success(
          `${item.name}${this.t(item.status === 'Enable' ? 'assistant.management.message.disabled' : 'assistant.management.message.enabled')}`
        );
      },
      error: () => {
        this.message.error(this.t('common.update-failed'));
      }
    });
  }

  getCreateModalTitleKey(): string {
    return this.editingAssistantId ? 'assistant.management.action.configure' : 'assistant.management.create.modal.title';
  }

  openCreateWizard(item?: AssistantItem, mode: AssistantWizardMode = item ? 'edit' : 'create'): void {
    this.createModalVisible = true;
    this.wizardMode = mode;
    this.editingAssistantId = item?.id ?? null;
    this.createStep = mode === 'renew' ? this.createSteps.length - 1 : 0;
    this.batchTriggerConditionMissing = false;
    this.resetAvatarPreview();
    this.avatarFile = null;
    this.avatarChanged = false;
    this.editingAvatarUrl = item?.avatarUrl ?? '';
    this.historyDays = 7;
    this.selectedPricingItemId = String(item?.pricingItemId ?? '').trim();
    this.payingOrderId = item?.orderId ?? null;
    this.pendingPreferredPricing = this.getAssistantSelectedPricing(item);
    this.pendingDeductionInputs = null;
    this.deductionInputs = { uupon: null };
    this.createForm.reset({
      avatar: item ? 'keep' : '',
      name: item?.name ?? '',
      description: item?.description ?? '',
      responseMode: item?.responseMode ?? 'Qa',
      webhookUrl: item?.webhookUrl ?? '',
      batchMessageCount: 10,
      batchIntervalMinutes: 60,
      batchKeywords: '',
      messageTypes: ['Text'],
      senderTypes: ['Customer'],
      replyReceiverTypes: []
    });
    if (item?.avatarUrl) {
      this.avatarPreview = item.avatarUrl;
      this.createForm.controls.avatar.setValue(item.avatarUrl);
    }
    this.cdr.detectChanges();
    this.loadCreatePaymentOptions();
  }

  closeCreateWizard(): void {
    this.createModalVisible = false;
    this.wizardMode = 'create';
    this.editingAssistantId = null;
    this.avatarFile = null;
    this.avatarChanged = false;
    this.editingAvatarUrl = '';
    this.payingOrderId = null;
    this.resetAvatarPreview();
  }

  previousCreateStep(): void {
    if (this.createStep > 0) this.createStep -= 1;
  }

  nextCreateStep(): void {
    if (!this.validateCurrentStep()) {
      this.message.warning(this.t('assistant.management.create.message.complete-required'));
      return;
    }
    if (this.createStep < this.lastCreateStepIndex) this.createStep += 1;
  }

  submitCreateAssistant(): void {
    if (this.submitLoading()) {
      return;
    }
    if (!this.validateCurrentStep()) {
      this.message.warning(this.t('assistant.management.create.message.complete-required'));
      return;
    }

    this.submitAssistant();
  }

  private setSubmitLoading(value: boolean): void {
    this.submitLoading.set(value);
  }

  onAvatarFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.message.warning(this.t('assistant.management.create.avatar.invalid-type'));
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.message.warning(this.t('assistant.management.create.avatar.invalid-size'));
      input.value = '';
      return;
    }

    this.revokeAvatarObjectUrl();
    this.avatarObjectUrl = URL.createObjectURL(file);
    this.avatarPreview = this.avatarObjectUrl;
    this.avatarFile = file;
    this.avatarChanged = true;
    this.createForm.controls.avatar.setValue(file.name);
    this.createForm.controls.avatar.markAsDirty();
    input.value = '';
  }

  ngOnDestroy(): void {
    if (this.keywordDebounceTimer) {
      clearTimeout(this.keywordDebounceTimer);
      this.keywordDebounceTimer = null;
    }
    this.stopCountdown();
    this.stopStoreRenewalRefresh();
    this.revokeAvatarObjectUrl();
    this.revokeStoreInstanceAvatarObjectUrl();
  }

  setResponseMode(mode: AssistantResponseMode): void {
    this.createForm.controls.responseMode.setValue(mode);
    if (mode !== 'Batch') {
      this.batchTriggerConditionMissing = false;
    }
  }

  isResponseMode(mode: AssistantResponseMode): boolean {
    return this.createForm.value.responseMode === mode;
  }

  isBatchMode(): boolean {
    return this.createForm.value.responseMode === 'Batch';
  }

  toggleOption(controlName: 'messageTypes' | 'senderTypes' | 'replyReceiverTypes', optionValue: string): void {
    const control = this.createForm.controls[controlName];
    const current = [...((control.value ?? []) as string[])];
    const idx = current.indexOf(optionValue);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(optionValue);
    }
    control.setValue(current);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  isOptionSelected(controlName: 'messageTypes' | 'senderTypes' | 'replyReceiverTypes', optionValue: string): boolean {
    const current = (this.createForm.controls[controlName].value ?? []) as string[];
    return current.includes(optionValue);
  }

  getSelectedLabels(controlName: 'messageTypes' | 'senderTypes' | 'replyReceiverTypes'): string {
    const selectedValues = (this.createForm.controls[controlName].value ?? []) as string[];
    if (controlName === 'replyReceiverTypes') {
      const labels = [this.t('assistant.management.create.sender-type.employee')];
      if (selectedValues.includes('Customer')) {
        labels.push(this.t('assistant.management.create.sender-type.customer'));
      }
      return labels.join('、');
    }
    const options = controlName === 'messageTypes' ? this.messageTypeOptions : this.senderTypeOptions;
    return options
      .filter(option => selectedValues.includes(option.value))
      .map(option => this.t(option.labelKey))
      .join('、');
  }

  getSelectedPlan(): PricingPlan | undefined {
    return this.getDisplayPricingPlans().find(plan => plan.id === this.selectedPricingItemId);
  }

  getSelectedPlanPriceText(): string {
    const plan = this.getSelectedPlan();
    if (!plan) return '-';
    return `${this.getPlanDurationText(plan)} · ${this.t('assistant.management.create.pay.approx')} ¥${plan.price.toFixed(2)}`;
  }

  getSelectedPlanCashText(): string {
    return `¥${this.getPayableCash().toFixed(2)}`;
  }

  getSettlementDetailRows(): SettlementDetailRow[] {
    return [
      {
        label: this.t('assistant.management.create.pay.summary.price'),
        value: `¥${this.getSelectedPlanPrice().toFixed(2)}`
      },
      {
        label: `${this.t('assistant.management.create.pay.balance.uupon')}（${this.getDeductPoints('uupon')}${this.t(
          'assistant.management.create.pay.points-unit'
        )}）`,
        value: `-¥${this.getDeductedCash('uupon').toFixed(2)}`
      }
    ];
  }

  getPreviewResponseLabel(): string {
    const mode = (this.createForm.value.responseMode ?? 'Qa') as AssistantResponseMode;
    return this.getResponseLabel(mode);
  }

  getHistoryRangeBackground(): string {
    const min = 1;
    const max = 30;
    const value = Math.min(max, Math.max(min, this.historyDays));
    const percent = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #02021a 0%, #02021a ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`;
  }

  isCurrentStepValid(): boolean {
    const controlsByStep: CreateFormControl[][] = [
      ['avatar', 'name', 'description'],
      ['responseMode', 'webhookUrl'],
      ['messageTypes', 'senderTypes', 'replyReceiverTypes'],
      []
    ];
    const controls = controlsByStep[this.createStep] ?? [];
    const baseValid = controls.every(key => this.createForm.controls[key].valid);
    if (this.createStep !== 1) return baseValid;
    return baseValid && this.validateBatchConfig(false);
  }

  selectPlan(planId: string): void {
    this.selectedPricingItemId = planId;
    this.deductionInputs = { uupon: null };
  }

  getDisplayPricingPlans(): PricingPlan[] {
    if (this.isPayWizard()) {
      const selectedPlan = this.pricingPlans().find(plan => plan.id === this.selectedPricingItemId);
      return selectedPlan ? [selectedPlan] : [];
    }
    return this.pricingPlans();
  }

  onPlanClick(plan: PricingPlan): void {
    if (this.isPayWizard()) {
      return;
    }
    if (!this.isTrialPlan(plan)) {
      this.selectPlan(plan.id);
      return;
    }
    this.modal.confirm({
      nzTitle: this.t('assistant.management.create.pay.trial.confirm.title'),
      nzContent: this.t('assistant.management.create.pay.trial.confirm.content'),
      nzOkText: this.t('common.confirm'),
      nzCancelText: this.t('assistant.management.create.footer.cancel'),
      nzOnOk: () => {
        this.selectPlan(plan.id);
        this.submitAssistant();
      }
    });
  }

  getBalancePoints(type: BalanceType): number {
    return this.balanceCards.find(item => item.type === type)?.points ?? 0;
  }

  getDeductPoints(type: BalanceType): number {
    return this.toNonNegativeInteger(this.deductionInputs[type]);
  }

  getDeductedCash(type: BalanceType): number {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return 0;
    const points = this.getDeductPoints(type);
    return points > 0 ? points / card.deductRate : 0;
  }

  getSelectedStorePackagePrice(): number {
    const app = this.selectedStoreApp();
    const selectedPackage = app ? this.getSelectedStorePackage(app) : null;
    return Number(selectedPackage?.priceCny ?? 0) || 0;
  }

  getStorePackageBalancePoints(type: BalanceType): number {
    return this.balanceCards.find(item => item.type === type)?.points ?? 0;
  }

  getStorePackageDeductPoints(type: BalanceType): number {
    return this.toNonNegativeInteger(this.deductionInputs[type]);
  }

  getStorePackageDeductedCash(type: BalanceType): number {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return 0;
    const points = this.getStorePackageDeductPoints(type);
    return points > 0 ? points / card.deductRate : 0;
  }

  getStorePackageTotalDeductedCash(): number {
    const total = this.getStorePackageDeductedCash('uupon');
    return Math.min(this.getSelectedStorePackagePrice(), total);
  }

  getStorePackagePayableCash(): number {
    return Math.max(0, this.getSelectedStorePackagePrice() - this.getStorePackageTotalDeductedCash());
  }

  onStorePackageDeductInput(type: BalanceType, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const integerText = input.value.replace(/\D+/g, '');
    this.applyStorePackageDeductInput(type, integerText);
    input.value = this.deductionInputs[type] === null ? '' : String(this.deductionInputs[type]);
  }

  applyStorePackageDeductInput(type: BalanceType, value: string | number | null | undefined): void {
    if (this.isStorePackageDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;

    const raw = Number(value ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.deductionInputs[type] = null;
      return;
    }

    const packagePrice = this.getSelectedStorePackagePrice();
    if (raw / card.deductRate >= packagePrice) {
      const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
      const points = Math.min(card.points, maxPointsByPackage);
      this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
      return;
    }

    const maxByBalance = card.points;
    const maxByPrice = Math.floor(this.getSelectedStorePackagePrice() * card.deductRate);
    const normalized = Math.floor(Math.min(raw, maxByBalance, maxByPrice));
    this.deductionInputs[type] = normalized > 0 ? normalized : null;
  }

  deductAllStorePackage(type: BalanceType): void {
    if (this.isStorePackageDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const packagePrice = this.getSelectedStorePackagePrice();
    const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
    const points = Math.min(card.points, maxPointsByPackage);
    this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
  }

  isStorePackageDeductInputDisabled(type: BalanceType): boolean {
    return this.getStorePackageBalancePoints(type) <= 0 || this.getSelectedStorePackagePrice() <= 0;
  }

  getTotalDeductedCash(): number {
    const total = this.getDeductedCash('uupon');
    return Math.min(this.getSelectedPlanPrice(), total);
  }

  getSelectedPlanPrice(): number {
    return Number(this.getSelectedPlan()?.price ?? 0) || 0;
  }

  isTrialPlan(plan?: PricingPlan | null): boolean {
    return !!plan && (plan.trial === true || plan.id === TRIAL_PACKAGE_ID);
  }

  getPlanSubText(plan: PricingPlan): string {
    if (this.isTrialPlan(plan)) {
      const trialDays = Number(plan.trialDays ?? 0) || 7;
      return `${trialDays} ${this.t('assistant.management.create.filter.day-unit')} · ${this.t('assistant.management.create.pay.trial.tag')}`;
    }
    return `${this.getPlanDurationText(plan)} (${this.t('assistant.management.create.pay.approx')} ¥${plan.price.toFixed(2)})`;
  }

  getPayableCash(): number {
    return Math.max(0, this.getSelectedPlanPrice() - this.getTotalDeductedCash());
  }

  blockNonIntegerInput(event: InputEvent): void {
    if (event.data && !/^\d+$/.test(event.data)) {
      event.preventDefault();
    }
  }

  onDeductInput(type: BalanceType, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const integerText = input.value.replace(/\D+/g, '');
    this.applyDeductInput(type, integerText);
    input.value = this.deductionInputs[type] === null ? '' : String(this.deductionInputs[type]);
  }

  applyDeductInput(type: BalanceType, value: string | number | null | undefined): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;

    const raw = Number(value ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.deductionInputs[type] = null;
      return;
    }

    const packagePrice = this.getSelectedPlanPrice();
    if (raw / card.deductRate >= packagePrice) {
      const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
      const points = Math.min(card.points, maxPointsByPackage);
      this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
      this.syncDeductionLock(type);
      return;
    }

    const maxByBalance = card.points;
    const maxByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const normalized = Math.floor(Math.min(raw, maxByBalance, maxByPrice));
    this.deductionInputs[type] = normalized > 0 ? normalized : null;
    this.syncDeductionLock(type);
  }

  deductAll(type: BalanceType): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const packagePrice = this.getSelectedPlanPrice();
    const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);

    if (card.points >= maxPointsByPackage) {
      this.deductionInputs[type] = this.normalizeUUPonMultiple(maxPointsByPackage);
      this.syncDeductionLock(type);
      return;
    }

    const maxPointsByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const points = Math.min(card.points, maxPointsByPrice);
    this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
    this.syncDeductionLock(type);
  }

  isDeductInputDisabled(type: BalanceType): boolean {
    return false;
  }

  configure(item: AssistantItem): void {
    if (item.source === 'Subscription') {
      const subscription = this.storeSubscriptions().find(candidate => candidate.assistantId === item.id) ?? {
        subscriptionId: '',
        assistantId: item.id,
        templateId: '',
        name: item.name,
        avatarUrl: item.avatarUrl,
        status: item.status === 'Enable' ? 'Active' : 'Expired'
      };
      this.openStoreInstanceDetail(subscription);
      return;
    }
    this.openCreateWizard(item, 'edit');
    this.loadAssistantDetail(item, false);
  }

  renew(item: AssistantItem): void {
    this.openCreateWizard(item, 'renew');
    this.loadAssistantDetail(item, true);
  }

  continuePay(item: AssistantItem): void {
    if (!this.canPayPendingAssistant(item)) {
      return;
    }
    const pendingSubscription = this.getPendingStoreSubscription(item);
    if (pendingSubscription) {
      if (this.isStorePendingPaymentExpired(pendingSubscription)) {
        this.rePayStoreSubscription(pendingSubscription);
        return;
      }
      this.continueStorePendingPayment(pendingSubscription);
      return;
    }
    if (this.isPendingPaymentExpired(item)) {
      this.reopenExpiredAssistantPurchase(item);
      return;
    }
    const orderId = this.getAssistantOrderId(item);
    if (!orderId) {
      this.message.warning(this.t('common.load-failed'));
      return;
    }
    if (this.openPaymentUrl(item, orderId)) {
      return;
    }
    this.payAssistantOrder(orderId, item.id);
  }

  private reopenExpiredAssistantPurchase(item: AssistantItem): void {
    if (item.source !== 'Subscription') {
      this.renew(item);
      return;
    }

    const reopenSubscription = (subscriptions: AssistantStoreSubscription[]): void => {
      const subscription = subscriptions.find(candidate => candidate.assistantId === item.id);
      if (!subscription) {
        this.message.warning(this.t('common.load-failed'));
        return;
      }
      this.rePayStoreSubscription(subscription);
    };
    const subscription = this.storeSubscriptions().find(candidate => candidate.assistantId === item.id);
    if (subscription) {
      reopenSubscription([subscription]);
      return;
    }
    this.loadAssistantStoreInstances(reopenSubscription);
  }

  isUnpaid(item: AssistantItem): boolean {
    if (this.getPendingStoreSubscription(item)) return true;
    const paymentStatus = String(item.paymentStatus ?? '')
      .trim()
      .toLowerCase();
    const latestOrderStatus = String(item.latestOrderStatus ?? '')
      .trim()
      .toLowerCase();
    if (this.isPendingOrderStatus(latestOrderStatus)) return true;
    if (paymentStatus === 'paid' || ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(latestOrderStatus))
      return false;
    if (this.isTrialAssistant(item)) return false;
    if (item.needPay === true) return true;
    return paymentStatus === 'unpaid' || paymentStatus === 'pending';
  }

  isPendingPaymentExpired(item: AssistantItem): boolean {
    if (!this.isUnpaid(item)) return false;
    const paymentSource = this.getPendingStoreSubscription(item) ?? item;
    const remainingMs = getPaymentRemainingMs(paymentSource, this.now);
    if (remainingMs !== null) return remainingMs <= 0;
    const latestOrderStatus = String(item.latestOrderStatus ?? '')
      .trim()
      .toLowerCase();
    const paymentStatus = String(item.paymentStatus ?? '')
      .trim()
      .toLowerCase();
    return ['failed', 'canceled', 'cancelled', 'expired', 'closed', 'replaced'].includes(latestOrderStatus) || paymentStatus === 'expired';
  }

  canPayPendingAssistant(item: AssistantItem): boolean {
    return this.isUnpaid(item);
  }

  getPendingPaymentCountdown(item: AssistantItem): string {
    if (!this.isUnpaid(item) || this.isPendingPaymentExpired(item)) return '';
    const paymentSource = this.getPendingStoreSubscription(item) ?? item;
    const remainingMs = getPaymentRemainingMs(paymentSource, this.now);
    if (remainingMs === null) return '';
    return formatCountdown(remainingMs);
  }

  isTrialAssistant(item: AssistantItem): boolean {
    return this.parseTrialFlag(item.trial);
  }

  isExpiredAssistant(item: AssistantItem): boolean {
    if (item.source === 'Subscription' && String(item.subscriptionStatus ?? '').toLowerCase() === 'expired') {
      return true;
    }
    const expireTime = this.getAssistantExpireTimestamp(item);
    return expireTime !== null && expireTime < Date.now();
  }

  canRenewAssistant(item: AssistantItem): boolean {
    if (this.isTrialAssistant(item)) return false;
    if (this.isUnpaid(item)) return false;
    return item.paymentStatus === 'Paid' || this.isExpiredAssistant(item);
  }

  shouldOfferSubscriptionRenewal(item: AssistantItem): boolean {
    if (item.source !== 'Subscription' || !this.isExpiredAssistant(item)) {
      return false;
    }
    const subscription = this.storeSubscriptions().find(candidate => candidate.assistantId === item.id);
    return item.canRenew === true || (!!subscription && this.canRenewStoreSubscription(subscription));
  }

  openSubscriptionRenewalPurchase(item: AssistantItem): void {
    const subscription = this.storeSubscriptions().find(candidate => candidate.assistantId === item.id);
    if (!this.shouldOfferSubscriptionRenewal(item)) {
      this.message.warning(this.t('common.load-failed'));
      return;
    }
    if (subscription) {
      this.openStoreRenewPackageModal(subscription);
      return;
    }

    this.loadAssistantStoreInstances(subscriptions => {
      const loadedSubscription = subscriptions.find(candidate => candidate.assistantId === item.id);
      if (!loadedSubscription) {
        this.message.warning(this.t('common.load-failed'));
        return;
      }
      this.openStoreRenewPackageModal(loadedSubscription);
    });
  }

  getAssistantExpireDateText(item: AssistantItem): string {
    const expireTime = this.getAssistantExpireTimestamp(item);
    if (expireTime === null) {
      return '-';
    }
    const date = new Date(expireTime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getAssistantRemainingDays(item: AssistantItem): number | null {
    const expireTime = this.getAssistantExpireTimestamp(item);
    if (expireTime === null) {
      return null;
    }
    const diff = expireTime - Date.now();
    if (diff <= 0) {
      return 0;
    }
    return Math.ceil(diff / (24 * 60 * 60 * 1000));
  }

  getAssistantRemainingText(item: AssistantItem): string {
    const remainingDays = this.getAssistantRemainingDays(item);
    if (remainingDays === null) {
      return '-';
    }
    if (remainingDays <= 0) {
      return this.t('robot-service.my.expired');
    }
    return `${remainingDays} ${this.t('robot-service.my.days')}`;
  }

  canEnableAssistant(item: AssistantItem): boolean {
    if (item.status !== 'Disable') return false;
    if (this.isTrialAssistant(item)) return true;
    if (this.isUnpaid(item)) return false;
    if (this.isExpiredAssistant(item)) return false;
    return true;
  }

  canDisableAssistant(item: AssistantItem): boolean {
    return item.status === 'Enable';
  }

  canDeleteAssistant(item: AssistantItem): boolean {
    return item.source === 'Custom';
  }

  remove(item: AssistantItem): void {
    this.modal.confirm({
      nzTitle: this.t('assistant.management.delete.confirm.first.title'),
      nzContent: this.t('assistant.management.delete.confirm.first.content', { name: item.name }),
      nzOkText: this.t('common.confirm'),
      nzCancelText: this.t('common.cancel'),
      nzClassName: 'assistant-delete-confirm-modal',
      nzOnOk: () => this.confirmRemove(item)
    });
  }

  private confirmRemove(item: AssistantItem): void {
    this.modal.confirm({
      nzTitle: this.t('assistant.management.delete.confirm.second.title'),
      nzContent: this.t('assistant.management.delete.confirm.second.content', { name: item.name }),
      nzOkText: this.t('common.confirm'),
      nzCancelText: this.t('common.cancel'),
      nzOkDanger: true,
      nzClassName: 'assistant-delete-confirm-modal',
      nzOnOk: () => this.executeRemove(item)
    });
  }

  private executeRemove(item: AssistantItem): void {
    const api = ConstantUtil.TENANT_ASSISTANTS_V1_DELETE.replace('{assistantId}', item.id);
    this.http.post(api, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          showFallbackHttpError(this.message, res?.msg, this.t('delete-failed'));
          return;
        }
        this.assistants.update(list => list.filter(cur => cur.id !== item.id));
        this.total = Math.max(0, this.total - 1);
        this.message.success(`${this.t('assistant.management.message.deleted')}：${item.name}`);
      },
      error: () => {
        this.message.error(this.t('delete-failed'));
      }
    });
  }

  trackById(_: number, item: AssistantItem): string {
    return item.id;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  private getAssistantExpireTimestamp(item: AssistantItem): number | null {
    const expireTime = Number(item.subscriptionValidUntil ?? item.expireTime ?? 0);
    if (!Number.isFinite(expireTime) || expireTime <= 0) {
      return null;
    }
    return expireTime;
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.countdownTimer = window.setInterval(() => {
      if (this.assistantStoreView() === 'market') {
        return;
      }
      this.now = Date.now();
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private reloadAssistantList(resetPage: boolean): void {
    if (resetPage) {
      this.pageIndex = 1;
      this.hasMore = true;
    }
    this.refreshTime = Date.now();
    this.loadAssistantList();
  }

  private loadAssistantList(append = false): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading.set(true);
    }
    const payload: AssistantListPayload = {
      pageIndex: Math.max(0, this.pageIndex - 1),
      pageSize: this.pageSize,
      direction: this.direction,
      orderBy: this.orderBy,
      key: this.keyword().trim() || undefined,
      refreshTime: this.refreshTime,
      source: this.toApiSource(this.selectedSource()),
      status: this.toApiStatus(this.selectedStatus()),
      pricingCode: ASSISTANT_PRICING_CODE
    };

    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_V1_LIST, payload)
      .pipe(
        finalize(() => {
          if (append) {
            this.loadingMore = false;
          } else {
            this.loading.set(false);
          }
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            if (!append) {
              this.assistants.set([]);
              this.total = 0;
            }
            this.hasMore = false;
            this.trialUsed = true;
            this.trialStateKnown = true;
            return;
          }
          const data = res.data as AssistantListData;
          this.trialUsed = this.parseTrialUsed(data?.trialUsed);
          this.trialStateKnown = true;
          const rawItems = data.items ?? [];
          const list = rawItems.map(item => this.mapAssistant(item));
          this.total = Number(data.totalCount ?? 0) || 0;
          if (append) {
            this.assistants.update(current => [...current, ...list]);
          } else {
            this.assistants.set(list);
          }
          if (typeof data.hasNextPage === 'boolean') {
            this.hasMore = data.hasNextPage;
          } else {
            this.hasMore = this.pageIndex * this.pageSize < this.total && list.length > 0;
          }
        },
        error: () => {
          if (!append) {
            this.assistants.set([]);
            this.total = 0;
          }
          this.hasMore = false;
          this.trialUsed = true;
          this.trialStateKnown = true;
        }
      });
  }

  loadAssistantStoreCatalog(): void {
    this.storeLoading.set(true);
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_CATALOG, {
        pageIndex: 0,
        pageSize: 100,
        direction: 'desc',
        orderBy: 'publishTime',
        refreshTime: Date.now()
      })
      .pipe(finalize(() => this.storeLoading.set(false)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            this.marketApps.set([]);
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const data = (res?.data ?? {}) as AssistantStoreListData<AssistantStoreApp>;
          this.marketApps.set(data.items ?? []);
        },
        error: () => {
          this.marketApps.set([]);
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  loadAssistantStoreInstances(onLoaded?: (subscriptions: AssistantStoreSubscription[]) => void): void {
    this.storeLoading.set(true);
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_INSTANCES, {
        pageIndex: 0,
        pageSize: 100,
        direction: 'desc',
        orderBy: 'updateTime',
        refreshTime: Date.now()
      })
      .pipe(finalize(() => this.storeLoading.set(false)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            this.storeSubscriptions.set([]);
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const data = (res?.data ?? {}) as AssistantStoreListData<AssistantStoreSubscription>;
          const subscriptions = data.items ?? [];
          this.storeSubscriptions.set(subscriptions);
          onLoaded?.(subscriptions);
        },
        error: () => {
          this.storeSubscriptions.set([]);
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  startAssistantStoreTrial(app: AssistantStoreApp): void {
    const templateId = this.getStoreTemplateId(app);
    if (!templateId) {
      this.message.error(this.t('common.load-failed'));
      return;
    }
    if (this.findStoreSubscription(templateId)) {
      this.selectAssistantStoreView('instances');
      return;
    }
    this.storeActionLoadingId.set(templateId);
    const idempotencyKey = this.getTrialIdempotencyKey(templateId);
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_TRIAL, { templateId, idempotencyKey })
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          this.trialIdempotencyKeys?.delete(templateId);
          this.message.success(this.t('assistant.management.store.message.trial-started'));
          this.closeAssistantStoreDetail();
          this.selectAssistantStoreView('instances');
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  startAssistantStorePurchase(subscription: AssistantStoreSubscription, durationPackage: AssistantStoreDurationPackage): void {
    if ((!subscription.canRenew && !this.isStorePendingPaymentExpired(subscription)) || !durationPackage.packageId) {
      return;
    }
    this.storeActionLoadingId.set(`${subscription.subscriptionId}-${durationPackage.durationDays}`);
    const renewal: AssistantStoreRenewalContext = {
      assistantId: subscription.assistantId,
      previous: subscription
    };
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_PURCHASE, {
        bizId: subscription.assistantId,
        aipoolTenantId: this.aipoolTenantId,
        templateId: subscription.templateId,
        durationPackageId: durationPackage.packageId,
        deductUUPonPoints: 0
      })
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const order = res?.data ?? {};
          this.handleAssistantStorePurchaseOrder(order, renewal);
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  openStoreRenewPackageModal(subscription: AssistantStoreSubscription): void {
    if (!subscription.canRenew && !this.isStorePendingPaymentExpired(subscription)) {
      return;
    }

    this.selectedStorePurchaseBizId.set(subscription.assistantId);
    const app = this.getStoreAppFromSubscription(subscription);
    if ((app.durationPackages ?? []).length > 0) {
      this.selectedStoreApp.set(app);
      this.selectDefaultStorePackage(app);
      this.loadBalanceSummary();
      this.deductionInputs = { uupon: null };
      this.storePackageModalVisible.set(true);
      return;
    }

    if (!subscription.templateId) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.storeActionLoadingId.set(`renew-${subscription.subscriptionId}`);
    const url = ConstantUtil.ADMIN_ASSISTANT_SUBSCRIPTION_V1_ITEM.replace('{templateId}', encodeURIComponent(subscription.templateId));
    this.http
      .post(url, {})
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const detail = (res?.data?.item ?? res?.data ?? {}) as Partial<AssistantStoreApp>;
          const merged = {
            ...app,
            ...detail,
            templateId: detail.templateId ?? subscription.templateId,
            name: detail.name ?? app.name
          } as AssistantStoreApp;
          if ((merged.durationPackages ?? []).length === 0) {
            this.message.error(this.t('common.load-failed'));
            return;
          }
          this.selectedStoreApp.set(merged);
          this.selectDefaultStorePackage(merged);
          this.loadBalanceSummary();
          this.deductionInputs = { uupon: null };
          this.storePackageModalVisible.set(true);
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  stopAssistantStoreSubscription(subscription: AssistantStoreSubscription): void {
    if (!subscription.assistantId) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.modal.confirm({
      nzTitle: this.t('assistant.management.store.confirm.disable.title'),
      nzContent: this.t('assistant.management.store.confirm.disable.content', {
        name: subscription.name || this.t('assistant.management.store.official-assistant')
      }),
      nzOkText: this.t('assistant.management.store.action.stop'),
      nzCancelText: this.t('common.cancel'),
      nzOkDanger: true,
      nzClassName: 'assistant-store-disable-modal',
      nzOnOk: () => this.executeStopAssistantStoreSubscription(subscription)
    });
  }

  enableAssistantStoreSubscription(subscription: AssistantStoreSubscription): void {
    if (!this.canEnableStoreSubscription(subscription)) {
      this.message.error(this.t('common.load-failed'));
      return;
    }

    this.storeActionLoadingId.set(`enable-${subscription.assistantId}`);
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_V1_STATUS, { id: subscription.assistantId, status: 'Enable' })
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          this.message.success(this.t('assistant.management.message.enabled'));
          this.closeStoreInstanceDetail();
          this.loadAssistantStoreInstances();
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  private executeStopAssistantStoreSubscription(subscription: AssistantStoreSubscription): void {
    this.storeActionLoadingId.set(`stop-${subscription.assistantId}`);
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_V1_STATUS, { id: subscription.assistantId, status: 'Disable' })
      .pipe(finalize(() => this.storeActionLoadingId.set(null)))
      .subscribe({
        next: res => {
          if (!this.isHttpSuccess(res)) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          this.message.success(this.t('assistant.management.store.message.disabled'));
          this.closeAssistantStoreDetail();
          this.loadAssistantStoreInstances();
        },
        error: () => this.message.error(this.t('common.load-failed'))
      });
  }

  getStorePackages(templateId: string): AssistantStoreDurationPackage[] {
    return (this.marketApps().find(item => this.getStoreTemplateId(item) === templateId)?.durationPackages ?? []).filter(
      item => !!item.packageId
    );
  }

  getSelectedStorePackage(app: AssistantStoreApp): AssistantStoreDurationPackage | null {
    const packageId = this.selectedStorePackageId();
    if (!packageId) {
      return null;
    }
    return (app.durationPackages ?? []).find(item => item.packageId === packageId) ?? null;
  }

  getStoreTemplateId(app: AssistantStoreApp): string {
    return String(app.templateId ?? app.id ?? '').trim();
  }

  private getTrialIdempotencyKey(templateId: string): string {
    if (!this.trialIdempotencyKeys) {
      this.trialIdempotencyKeys = new Map<string, string>();
    }
    const existingKey = this.trialIdempotencyKeys.get(templateId);
    if (existingKey) {
      return existingKey;
    }
    const idempotencyKey = IdUtil.fastSimpleUUID();
    this.trialIdempotencyKeys.set(templateId, idempotencyKey);
    return idempotencyKey;
  }

  getStoreAppFromSubscription(subscription: AssistantStoreSubscription): AssistantStoreApp {
    const marketApp = this.marketApps().find(item => this.getStoreTemplateId(item) === subscription.templateId);
    return {
      ...(marketApp ?? {}),
      templateId: subscription.templateId,
      name: marketApp?.name || subscription.templateName || subscription.name || this.t('assistant.management.store.official-assistant'),
      providerName: marketApp?.providerName || subscription.providerName,
      shortDescription: marketApp?.shortDescription || subscription.configSummary || subscription.triggerSummary
    } as AssistantStoreApp;
  }

  getStoreInstanceName(subscription: AssistantStoreSubscription): string {
    return subscription.name || subscription.templateName || this.t('assistant.management.store.official-assistant');
  }

  getStoreInstanceTemplateName(subscription: AssistantStoreSubscription): string {
    return subscription.templateName || subscription.name || subscription.templateId || '-';
  }

  getStoreInstanceSubtitle(subscription: AssistantStoreSubscription): string {
    return subscription.description || subscription.serviceNumberName || subscription.triggerSummary || subscription.configSummary || '-';
  }

  getStoreInstanceStatusFilterCount(filter: AssistantStoreInstanceFilter): number {
    const stats = this.storeInstanceStats();
    const counts: Record<AssistantStoreInstanceFilter, number> = {
      All: stats.total,
      Trialing: stats.trialing,
      Active: stats.active,
      Expiring: stats.expiring,
      Expired: stats.expired,
      Disabled: stats.disabled,
      Purged: stats.purged
    };
    return counts[filter];
  }

  getStoreInstanceStatusHint(subscription: AssistantStoreSubscription): string {
    if (this.isStoreSubscriptionPendingPayment(subscription)) {
      return '';
    }
    if (this.isExpiringStoreSubscription(subscription)) {
      return this.t('assistant.management.store.instance.expiring-hint');
    }
    if (subscription.status === 'Expired' && subscription.dataRetentionUntil) {
      return `${this.t('assistant.management.store.retained-until')} ${this.formatStoreDateOnly(subscription.dataRetentionUntil)}，${this.t(
        'assistant.management.store.renew-to-restore'
      )}`;
    }
    if (subscription.status === 'Purged') {
      return this.t('assistant.management.store.instance.purged-hint');
    }
    return '';
  }

  getStoreCreateMethodLabel(method?: AssistantStoreSubscription['createMethod']): string {
    const labels: Record<string, string> = {
      Trial: 'assistant.management.store.create-method.trial',
      Purchase: 'assistant.management.store.create-method.purchase',
      Official: 'assistant.management.store.create-method.official',
      CustomCreate: 'assistant.management.store.create-method.custom'
    };
    return this.t(labels[method ?? ''] ?? 'assistant.management.store.create-method.purchase');
  }

  getStoreValidityRange(subscription: AssistantStoreSubscription): string {
    return `${this.formatStoreDateOnly(subscription.validFrom)} ${this.t('assistant.management.store.to')} ${this.formatStoreDateOnly(
      subscription.validUntil
    )}`;
  }

  getStoreLatestPackageLabel(subscription: AssistantStoreSubscription, detail?: AssistantDetailData | null): string {
    const lastOrder = detail?.lastOrder;
    const snapshot = this.parseAssistantOrderSnapshot(lastOrder?.itemSnapshotJson);
    const packageName = String(
      subscription.latestPackageName ?? snapshot?.['durationPackageName'] ?? snapshot?.['packageName'] ?? snapshot?.['name'] ?? ''
    ).trim();
    const durationDays = Number(
      subscription.latestPackageDurationDays ??
        lastOrder?.durationDays ??
        detail?.currentPricingDurationDays ??
        snapshot?.['durationDays'] ??
        0
    );
    if (packageName && durationDays > 0)
      return `${packageName} (${durationDays} ${this.t('assistant.management.store.instance-detail.days')})`;
    if (packageName) return packageName;
    if (durationDays > 0) return `${durationDays} ${this.t('assistant.management.store.instance-detail.days')}`;
    return '-';
  }

  private parseAssistantOrderSnapshot(value?: string): Record<string, unknown> | null {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  canRecoverStoreSubscription(subscription: AssistantStoreSubscription): boolean {
    return subscription.status === 'Expired' && !!subscription.dataRetentionUntil && subscription.dataRetentionUntil > Date.now();
  }

  canRenewStoreSubscription(subscription: AssistantStoreSubscription): boolean {
    return !!subscription.canRenew && subscription.status !== 'Purged' && !this.isStoreSubscriptionPendingPayment(subscription);
  }

  isStoreSubscriptionPendingPayment(subscription: AssistantStoreSubscription): boolean {
    const latestOrderStatus = String(subscription.latestOrderStatus ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    if (['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(latestOrderStatus)) {
      return false;
    }
    if (subscription.status === 'PendingPayment' || this.isPendingOrderStatus(latestOrderStatus)) {
      return true;
    }
    const hasPayableOrder = !!String(subscription.latestOrderId ?? '').trim() && !!resolvePaymentExpireTime(subscription);
    return hasPayableOrder && !['failed', 'canceled', 'cancelled', 'expired', 'closed', 'replaced'].includes(latestOrderStatus);
  }

  private getPendingStoreSubscription(item: AssistantItem): AssistantStoreSubscription | undefined {
    if (item.source !== 'Subscription') return undefined;
    return this.storeSubscriptions().find(
      subscription => subscription.assistantId === item.id && this.isStoreSubscriptionPendingPayment(subscription)
    );
  }

  isStorePendingPaymentExpired(subscription: AssistantStoreSubscription): boolean {
    if (!this.isStoreSubscriptionPendingPayment(subscription)) return false;
    const remainingMs = getPaymentRemainingMs(subscription, this.now);
    return remainingMs !== null && remainingMs <= 0;
  }

  getStorePendingPaymentCountdown(subscription: AssistantStoreSubscription): string {
    if (!this.isStoreSubscriptionPendingPayment(subscription) || this.isStorePendingPaymentExpired(subscription)) return '';
    const remainingMs = getPaymentRemainingMs(subscription, this.now);
    return remainingMs === null ? '' : formatCountdown(remainingMs);
  }

  continueStorePendingPayment(subscription: AssistantStoreSubscription): void {
    if (!this.isStoreSubscriptionPendingPayment(subscription) || this.isStorePendingPaymentExpired(subscription)) return;
    const orderId = String(subscription.latestOrderId ?? '').trim();
    if (!orderId) {
      this.message.warning(this.t('common.load-failed'));
      return;
    }
    if (this.openPaymentUrl(subscription, orderId)) return;
    this.payAssistantOrder(orderId, subscription.assistantId);
  }

  rePayStoreSubscription(subscription: AssistantStoreSubscription): void {
    if (!this.isStorePendingPaymentExpired(subscription)) return;
    this.openStoreRenewPackageModal(subscription);
  }

  canStopStoreSubscription(subscription: AssistantStoreSubscription): boolean {
    return !!subscription.assistantId && (subscription.status === 'Trialing' || subscription.status === 'Active');
  }

  canEnableStoreSubscription(subscription: AssistantStoreSubscription): boolean {
    return (
      !!subscription.assistantId && subscription.status === 'Disabled' && (!subscription.validUntil || subscription.validUntil > Date.now())
    );
  }

  getStoreSubscriptionPowerAction(subscription: AssistantStoreSubscription): 'enable' | 'stop' | null {
    if (this.canEnableStoreSubscription(subscription)) {
      return 'enable';
    }
    if (this.canStopStoreSubscription(subscription)) {
      return 'stop';
    }
    return null;
  }

  isExpiringStoreSubscription(subscription: AssistantStoreSubscription): boolean {
    if (subscription.status !== 'Active' || !subscription.validUntil) {
      return false;
    }
    const remainingMs = subscription.validUntil - Date.now();
    return remainingMs > 0 && remainingMs <= 7 * 24 * 60 * 60 * 1000;
  }

  private isHiddenByStatusGroup(subscription: AssistantStoreSubscription, status: AssistantStoreInstanceFilter): boolean {
    return status === 'Active' && this.isExpiringStoreSubscription(subscription);
  }

  private isPendingOrderStatus(value?: string | null): boolean {
    const status = String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    return ['pending', 'pendingpayment', 'unpaid', 'awaitingpayment'].includes(status);
  }

  getStoreResponseModeLabel(mode?: AssistantResponseMode): string {
    const labels: Record<AssistantResponseMode, string> = {
      Qa: 'assistant.store.response.qa',
      CollectOnly: 'assistant.store.response.collect-only',
      Batch: 'assistant.store.response.batch'
    };
    return mode ? this.t(labels[mode]) : '-';
  }

  getStoreMessageTypesLabel(types?: string[]): string {
    const labels: Record<string, string> = {
      Text: 'assistant.store.message-type.text',
      Image: 'assistant.store.message-type.image',
      File: 'assistant.store.message-type.file',
      Audio: 'assistant.store.message-type.audio',
      Video: 'assistant.store.message-type.video'
    };
    return (types ?? []).map(item => this.t(labels[item] ?? item)).join('、') || '-';
  }

  getStoreCardFacts(app: AssistantStoreApp): AssistantStoreCardFact[] {
    const language = this.i18n.currentLang || '';
    const cached = this.storeCardFactsCache.get(app);
    if (cached?.language === language) {
      return cached.facts;
    }

    const facts: AssistantStoreCardFact[] = [];
    const responseMode = this.getStoreResponseModeLabel(app.responseMode);
    const messageTypes = this.getStoreMessageTypesLabel(app.messageTypes);
    const durationDays = [...new Set((app.durationPackages ?? []).map(item => Number(item.durationDays)).filter(days => days > 0))];

    if (responseMode !== '-') {
      facts.push({ icon: 'robot', label: responseMode });
    }
    if (messageTypes !== '-') {
      facts.push({ icon: 'message', label: messageTypes });
    }
    if (durationDays.length > 0) {
      facts.push({
        icon: 'calendar',
        label: `${durationDays.join(' / ')} ${this.t('assistant.management.store.unit.day')}`
      });
    }

    const resolvedFacts =
      facts.length > 0 ? facts : [{ icon: 'robot' as const, label: this.t('assistant.management.store.official-assistant') }];
    this.storeCardFactsCache.set(app, { language, facts: resolvedFacts });
    return resolvedFacts;
  }

  trackByStoreCardFact(_: number, fact: AssistantStoreCardFact): AssistantStoreCardFact['icon'] {
    return fact.icon;
  }

  getStoreSenderTypesLabel(types?: string[]): string {
    const labels: Record<string, string> = {
      Employee: 'assistant.store.sender-type.employee',
      Customer: 'assistant.store.sender-type.customer',
      Member: 'assistant.store.sender-type.member'
    };
    return (types ?? []).map(item => this.t(labels[item] ?? item)).join('、') || '-';
  }

  getStoreReplyReceiverTypesLabel(app: AssistantStoreApp): string {
    return this.getStoreSenderTypesLabel(this.getReplyReceiverTypesForDisplay(app));
  }

  isStoreBatchMode(app: Pick<AssistantStoreApp, 'responseMode'>): boolean {
    return (
      String(app.responseMode ?? '')
        .trim()
        .toLowerCase() === 'batch'
    );
  }

  getStoreTriggerKeywordsLabel(app: AssistantStoreApp): string {
    return app.batchTrigger?.triggerKeywords?.join('、') || '-';
  }

  getStorePackageHint(durationPackage: AssistantStoreDurationPackage): string {
    if (durationPackage.recommended) {
      return this.t('assistant.management.store.detail.package.recommended');
    }
    if (durationPackage.durationDays >= 365) {
      return this.t('assistant.management.store.detail.package.year-best');
    }
    return this.t('assistant.management.store.detail.package.standard');
  }

  findStoreSubscription(templateId: string): AssistantStoreSubscription | undefined {
    return this.storeSubscriptions().find(item => item.templateId === templateId);
  }

  getStoreStatusLabel(status: AssistantStoreSubscription['status']): string {
    const labels: Record<AssistantStoreSubscription['status'], string> = {
      PendingPayment: this.t('assistant.management.store.status.pending-payment'),
      Trialing: this.t('assistant.management.store.status.trialing'),
      Active: this.t('assistant.management.store.status.active'),
      Expired: this.t('assistant.management.store.status.expired'),
      Disabled: this.t('assistant.management.store.status.disabled'),
      Purged: this.t('assistant.management.store.status.purged')
    };
    return labels[status] ?? status;
  }

  getStoreStatusColor(status: AssistantStoreSubscription['status']): string {
    const colors: Record<AssistantStoreSubscription['status'], string> = {
      PendingPayment: 'gold',
      Trialing: 'blue',
      Active: 'green',
      Expired: 'orange',
      Disabled: 'default',
      Purged: 'default'
    };
    return colors[status] ?? 'default';
  }

  formatStoreDate(value?: number): string {
    if (!value) return '-';
    return new Date(value).toLocaleString(this.i18n.currentLang || 'zh-TW', { hour12: false });
  }

  formatStoreDateOnly(value?: number): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(this.i18n.currentLang || 'zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  formatStorePrice(priceCny?: number): string {
    return new Intl.NumberFormat(this.i18n.currentLang || 'zh-TW', { style: 'currency', currency: 'TWD' }).format(Number(priceCny) || 0);
  }

  private selectDefaultStorePackage(app: AssistantStoreApp): void {
    const packages = (app.durationPackages ?? []).filter(item => !!item.packageId);
    const selectedId = this.selectedStorePackageId();
    if (selectedId && packages.some(item => item.packageId === selectedId)) {
      return;
    }
    const defaultPackage = packages.find(item => item.recommended) ?? packages[0];
    this.selectedStorePackageId.set(defaultPackage?.packageId ?? null);
  }

  private isHttpSuccess(res: any): boolean {
    return !!res?.success || res?.status === 0 || res?.status === 200;
  }

  private loadCreatePaymentOptions(): void {
    this.loadBalanceSummary();
    this.loadAssistantPackages();
  }

  private loadBalanceSummary(): void {
    this.balanceCards = this.buildBalanceCards();
    this.http.post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_POINTS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) return;
        this.aipoolTenantId = this.resolveAipoolTenantId(res?.data);
        this.balanceCards = this.buildBalanceCards(res?.data);
        this.deductionInputs = { uupon: null };
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  private toApiSource(source: 'All' | AssistantSource): AssistantSource | undefined {
    if (source === 'Custom') return 'Custom';
    if (source === 'Subscription') return 'Subscription';
    return undefined;
  }

  private toApiStatus(status: 'All' | AssistantStatus): AssistantStatus | undefined {
    if (status === 'Enable') return 'Enable';
    if (status === 'Disable') return 'Disable';
    return undefined;
  }

  private buildCreateAssistantPayload(): AssistantCreatePayload {
    const value = this.createForm.getRawValue();
    const stableAvatarUrl = this.editingAvatarUrl || (!this.avatarChanged ? this.avatarPreview : '');
    const selectedPlan = this.getSelectedPlan();
    const isTrial = this.isTrialPlan(selectedPlan);
    const selectedPricing = this.getPlanSelectedPricing(selectedPlan);
    const batchMessageCount = Number(value.batchMessageCount ?? 0);
    const batchIntervalMinutes = Number(value.batchIntervalMinutes ?? 0);
    const keywords = this.splitKeywords(value.batchKeywords);
    const responseMode = this.toApiResponseMode((value.responseMode as AssistantResponseMode) ?? 'Qa');
    const replyReceiverTypes = this.toApiReplyReceiverTypes((value.replyReceiverTypes as string[]) ?? []);
    const replyVisibility = this.toApiReplyVisibilityFromTargets(replyReceiverTypes);

    const payload: AssistantCreatePayload = {
      source: 'Custom',
      name: String(value.name ?? '').trim(),
      avatarUrl: this.avatarChanged ? '' : stableAvatarUrl || undefined,
      description: String(value.description ?? '').trim(),
      responseMode,
      webhookUrl: String(value.webhookUrl ?? '').trim(),
      messageTypes: this.toApiMessageTypes((value.messageTypes as string[]) ?? []),
      senderTypes: this.toApiSenderTypes((value.senderTypes as string[]) ?? []),
      replyVisibleFor: replyVisibility,
      replyVisibleScope: replyVisibility,
      replyVisibleTargets: this.toLegacyReplyVisibleTargets(replyReceiverTypes),
      replyReceiverTypes,
      historyDays: Math.max(1, Number(this.historyDays) || 1)
    };

    const assistantId = String(this.editingAssistantId ?? '').trim();
    if (assistantId) {
      payload.assistantId = assistantId;
    }
    if (selectedPricing?.pricingConfigId) {
      payload.pricingConfigId = selectedPricing.pricingConfigId;
    }
    if (selectedPricing?.pricingItemId) {
      payload.pricingItemId = selectedPricing.pricingItemId;
    }
    if (selectedPricing?.pricingCode) {
      payload.pricingCode = selectedPricing.pricingCode;
    }
    if (isTrial) {
      payload.useTrial = true;
      payload.deductUUPonPoints = 0;
    } else {
      payload.deductUUPonPoints = Math.max(0, Math.floor(this.getDeductPoints('uupon')));
    }
    const tenantId = this.getTenantId();
    if (tenantId) {
      payload.tenantId = tenantId;
    }
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }

    if (responseMode === 'Batch') {
      if (Number.isFinite(batchMessageCount) && batchMessageCount >= 1) {
        payload['batchTrigger.triggerMessageCount'] = Math.floor(batchMessageCount);
      }
      if (Number.isFinite(batchIntervalMinutes) && batchIntervalMinutes >= 1) {
        payload['batchTrigger.triggerIntervalSeconds'] = Math.floor(batchIntervalMinutes * 60);
      }
      if (keywords.length > 0) {
        payload['batchTrigger.triggerKeywords'] = keywords;
      }
    }

    return payload;
  }

  private buildRenewAssistantPayload(): AssistantCreatePayload | null {
    const assistantId = String(this.editingAssistantId ?? '').trim();
    const selectedPlan = this.getSelectedPlan();
    const selectedPricing = this.getPlanSelectedPricing(selectedPlan);
    if (!assistantId || !selectedPricing?.pricingConfigId || !selectedPricing.pricingItemId || !selectedPricing.pricingCode) {
      return null;
    }

    const payload: AssistantCreatePayload = {
      assistantId,
      pricingConfigId: selectedPricing.pricingConfigId,
      pricingItemId: selectedPricing.pricingItemId,
      pricingCode: selectedPricing.pricingCode,
      source: 'Custom',
      name: '',
      description: '',
      responseMode: 'Qa',
      webhookUrl: '',
      messageTypes: [],
      senderTypes: [],
      historyDays: 1,
      deductUUPonPoints: Math.max(0, Math.floor(this.getDeductPoints('uupon')))
    };

    const tenantId = this.getTenantId();
    if (tenantId) {
      payload.tenantId = tenantId;
    }
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    return payload;
  }

  private submitAssistant(): void {
    if (this.submitLoading()) {
      return;
    }
    if (!this.validateUUPonMultiple()) {
      return;
    }
    if (this.isPayWizard()) {
      if (!this.payingOrderId) {
        this.message.warning(this.t('assistant.management.create.message.complete-required'));
        return;
      }
      this.payAssistantOrder(this.payingOrderId);
      return;
    }

    if (this.isRenewWizard()) {
      const renewPayload = this.buildRenewAssistantPayload();
      if (!renewPayload) {
        this.message.warning(this.t('assistant.management.create.message.complete-required'));
        return;
      }

      let handOffToPay = false;
      this.setSubmitLoading(true);
      this.http
        .post(ConstantUtil.ADMIN_ASSISTANT_ORDER_V1_RENEW, renewPayload)
        .pipe(
          finalize(() => {
            if (!handOffToPay) {
              this.setSubmitLoading(false);
            }
          })
        )
        .subscribe({
          next: res => {
            const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
            if (!isSuccess) {
              showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
              return;
            }
            const data = (res?.data ?? {}) as AssistantOrderData;
            const orderId = this.getAssistantOrderId(data);
            if (data.payRequired && orderId) {
              handOffToPay = true;
              this.payAssistantOrder(orderId);
              this.message.success(this.t('assistant.management.create.message.success'));
              return;
            }
            if (this.openPaymentUrl(data, orderId ?? undefined)) {
              this.message.success(this.t('assistant.management.create.message.success'));
              return;
            } else {
              this.message.success(this.t('assistant.management.create.message.success'));
              this.createModalVisible = false;
              this.wizardMode = 'create';
              this.editingAssistantId = null;
              this.payingOrderId = null;
              this.avatarFile = null;
              this.avatarChanged = false;
              this.editingAvatarUrl = '';
              this.reloadAssistantList(true);
            }
          },
          error: () => {
            this.message.error(this.t('common.load-failed'));
          }
        });
      return;
    }

    const payload = this.buildCreateAssistantPayload();
    const isCreate = !String(payload.assistantId ?? '').trim();
    const isEditOnly = this.isEditWizard();
    const hasAvatarUrl = String(payload.avatarUrl ?? '').trim().length > 0;
    const hasAvatarFile = !!this.avatarFile;
    if (this.avatarChanged && !hasAvatarFile) {
      this.message.warning(this.t('assistant.management.create.basic.avatar.required'));
      return;
    }
    if (isCreate && !hasAvatarUrl && !hasAvatarFile) {
      this.message.warning(this.t('assistant.management.create.basic.avatar.required'));
      return;
    }
    if (!isEditOnly && (!payload.pricingConfigId || !payload.pricingItemId || !payload.pricingCode)) {
      this.message.warning(this.t('assistant.management.create.message.complete-required'));
      return;
    }
    let handOffToPay = false;
    const submitApi = isEditOnly ? ConstantUtil.ADMIN_ASSISTANT_V1_UPDATE : ConstantUtil.ADMIN_ASSISTANT_ORDER_V1_CREATE;
    this.setSubmitLoading(true);
    this.http
      .postFormData(submitApi, payload, this.avatarChanged ? this.avatarFile : null, 'avatarFile')
      .pipe(
        finalize(() => {
          if (!handOffToPay) {
            this.setSubmitLoading(false);
          }
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const data = (res?.data ?? {}) as AssistantOrderData;
          const orderId = this.getAssistantOrderId(data);
          if (!isEditOnly && data.payRequired && orderId) {
            handOffToPay = true;
            this.payAssistantOrder(orderId);
            this.message.success(this.t('assistant.management.create.message.success'));
            return;
          }
          if (!isEditOnly && this.openPaymentUrl(data, orderId ?? undefined)) {
            this.message.success(this.t('assistant.management.create.message.success'));
            return;
          }
          this.message.success(this.t(isEditOnly ? 'common.update-success' : 'assistant.management.create.message.success'));
          this.createModalVisible = false;
          this.wizardMode = 'create';
          this.editingAssistantId = null;
          this.payingOrderId = null;
          this.avatarFile = null;
          this.avatarChanged = false;
          this.editingAvatarUrl = '';
          this.reloadAssistantList(true);
        },
        error: () => {
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  private getTenantId(): string {
    const routeTenantId = this.route.snapshot.paramMap.get('id');
    if (routeTenantId) return routeTenantId;
    return String(localStorage.getItem('current_tenant_id') ?? '');
  }

  private mapAssistant(item: AssistantListItem): AssistantItem {
    const avatarUrl = this.resolveAvatarUrl(item);
    const selectedPricing = this.getAssistantSelectedPricing(item);
    const latestOrderId = this.getAssistantOrderId(item);
    const lastOrderId = String(item?.lastOrder?.orderId ?? '').trim();
    const mappedOrderId = latestOrderId ?? (lastOrderId || undefined);
    const paymentUrl = String(item.paymentUrl ?? item.lastOrder?.paymentUrl ?? '').trim();
    return {
      id: String(item.id ?? ''),
      orderId: mappedOrderId,
      paymentUrl: paymentUrl || undefined,
      name: String(item.name ?? ''),
      source: this.fromApiSource(item.source),
      status: this.fromApiStatus(item.status),
      paymentStatus: item.paymentStatus,
      needPay: item.needPay === true,
      trial: this.parseTrialFlag(item.trial),
      expireTime: Number(item.expireTime ?? 0) || undefined,
      responseMode: this.fromApiResponseMode(item.responseMode),
      description: String(item.description ?? ''),
      webhookUrl: String(item.webhookUrl ?? ''),
      providerName: String(item.providerName ?? '').trim() || undefined,
      pricingConfigId: selectedPricing?.pricingConfigId || undefined,
      pricingItemId: selectedPricing?.pricingItemId || undefined,
      pricingCode: selectedPricing?.pricingCode || undefined,
      avatarUrl,
      reserveTimeoutSeconds: item.reserveTimeoutSeconds,
      payStartTime: item.payStartTime,
      payExpireTime: item.payExpireTime,
      latestOrderStatus: item.latestOrderStatus,
      subscriptionId: item.subscriptionId,
      subscriptionStatus: item.subscriptionStatus,
      subscriptionValidUntil: Number(item.subscriptionValidUntil ?? 0) || undefined,
      canRenew: item.canRenew === true
    };
  }

  private loadAssistantDetail(item: AssistantItem, openPayStep: boolean): void {
    const api = ConstantUtil.ADMIN_ASSISTANT_V1_ITEM.replace('{assistantId}', item.id);
    this.http.post(api, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
          return;
        }
        const detail = (res?.data ?? {}) as AssistantDetailData;
        const merged = this.mergeAssistantDetail(item, detail);
        this.ngZone.run(() => {
          const preferredPricing = this.getAssistantSelectedPricing(merged);
          this.editingAssistantId = merged.id;
          this.editingAvatarUrl = merged.avatarUrl ?? '';
          this.payingOrderId = merged.orderId ?? null;
          this.pendingPreferredPricing = preferredPricing;
          this.createForm.patchValue({
            avatar: this.createForm.controls.avatar.value || (merged.avatarUrl ? 'keep' : ''),
            name: merged.name ?? '',
            description: merged.description ?? '',
            responseMode: merged.responseMode ?? 'Qa',
            webhookUrl: merged.webhookUrl ?? ''
          });
          if (merged.avatarUrl && !this.avatarChanged) {
            this.avatarPreview = merged.avatarUrl;
            this.createForm.controls.avatar.setValue('keep');
          }
          this.selectedPricingItemId = this.resolvePreferredPricingItemId(preferredPricing) ?? this.selectedPricingItemId;
          this.applyAssistantDetail(detail);
          if (openPayStep) {
            this.createStep = this.createSteps.length - 1;
          }
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.message.error(this.t('common.load-failed'));
      }
    });
  }

  private mergeAssistantDetail(base: AssistantItem, detail: AssistantDetailData): AssistantItem {
    const detailAvatarUrl = this.resolveAvatarUrl(detail) ?? base.avatarUrl;
    const detailPricing = this.getAssistantSelectedPricing(detail);
    return {
      ...base,
      orderId: this.getAssistantOrderId(detail) ?? base.orderId,
      name: String(detail.name ?? base.name),
      description: String(detail.description ?? base.description),
      webhookUrl: String(detail.webhookUrl ?? base.webhookUrl),
      responseMode: this.fromApiResponseMode(detail.responseMode),
      pricingConfigId: detailPricing?.pricingConfigId || base.pricingConfigId,
      pricingItemId: detailPricing?.pricingItemId || base.pricingItemId,
      pricingCode: detailPricing?.pricingCode || base.pricingCode,
      avatarUrl: detailAvatarUrl,
      paymentStatus: detail.paymentStatus ?? base.paymentStatus,
      needPay: detail.needPay === true || base.needPay === true,
      trial: this.parseTrialFlag(detail.trial ?? base.trial),
      expireTime: Number(detail.expireTime ?? base.expireTime ?? 0) || undefined,
      reserveTimeoutSeconds: detail.reserveTimeoutSeconds ?? base.reserveTimeoutSeconds,
      payStartTime: detail.payStartTime ?? base.payStartTime,
      payExpireTime: detail.payExpireTime ?? base.payExpireTime,
      paymentUrl: String(detail.paymentUrl ?? detail.lastOrder?.paymentUrl ?? base.paymentUrl ?? '').trim() || undefined,
      latestOrderStatus: detail.latestOrderStatus ?? base.latestOrderStatus
    };
  }

  private applyAssistantDetail(detail: AssistantDetailData): void {
    const messageTypes = this.fromApiMessageTypes(detail.messageTypes);
    const senderTypes = this.fromApiSenderTypes(detail.senderTypes);
    const replyReceiverTypes = this.resolveReplyReceiverTypes(detail);
    this.createForm.controls.messageTypes.setValue(messageTypes.length > 0 ? messageTypes : ['Text']);
    this.createForm.controls.senderTypes.setValue(senderTypes.length > 0 ? senderTypes : ['Customer']);
    this.createForm.controls.replyReceiverTypes.setValue(replyReceiverTypes);
    this.historyDays = Math.min(30, Math.max(1, Number(detail.historyDays ?? this.historyDays) || this.historyDays));

    const trigger = detail.batchTrigger;
    this.createForm.controls.batchMessageCount.setValue(trigger?.triggerMessageCount ?? 10);
    const intervalSeconds = Number(trigger?.triggerIntervalSeconds ?? 0);
    this.createForm.controls.batchIntervalMinutes.setValue(intervalSeconds >= 1 ? Math.max(1, Math.floor(intervalSeconds / 60)) : 60);
    this.createForm.controls.batchKeywords.setValue((trigger?.triggerKeywords ?? []).join(','));

    const uuponPrimary = this.resolveExplicitDeductPoint(detail.deductUUPonPoints);
    const uuponFallback = this.resolveDeductPoints([detail.usedUUPonPoints, detail.uuponPoints]);
    const inputs: Record<BalanceType, number | null> = {
      uupon: uuponPrimary ?? (uuponFallback > 0 ? uuponFallback : null)
    };
    this.pendingDeductionInputs = inputs;
    this.deductionInputs = { ...inputs };
  }

  private fromApiSource(source?: string): AssistantSource {
    const normalized = String(source ?? '')
      .trim()
      .toLowerCase();
    return normalized === 'subscription' ? 'Subscription' : 'Custom';
  }

  private fromApiStatus(status?: string): AssistantStatus {
    const normalized = String(status ?? '')
      .trim()
      .toLowerCase();
    return normalized === 'disable' ? 'Disable' : 'Enable';
  }

  private fromApiResponseMode(responseMode?: AssistantResponseMode): AssistantResponseMode {
    const normalized = String(responseMode ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'collectonly' || normalized === 'collect_only') return 'CollectOnly';
    if (normalized === 'batch') return 'Batch';
    return 'Qa';
  }

  private toApiResponseMode(mode: AssistantResponseMode): 'Qa' | 'CollectOnly' | 'Batch' {
    if (mode === 'CollectOnly') return 'CollectOnly';
    if (mode === 'Batch') return 'Batch';
    return 'Qa';
  }

  private toApiReplyVisibilityFromTargets(values: string[]): ReplyVisibility {
    const normalized = values.map(item => item.toLowerCase());
    return normalized.includes('customer') ? 'Both' : 'Employee';
  }

  private toApiReplyReceiverTypes(values: string[]): string[] {
    const list = Array.isArray(values) ? values : [values as unknown as string];
    return list
      .map(item => String(item ?? '').trim())
      .filter(item => item.toLowerCase() === 'customer')
      .map(() => 'Customer');
  }

  private toLegacyReplyVisibleTargets(values: string[]): string[] {
    return values.some(item => item.toLowerCase() === 'customer') ? ['Employee', 'Customer'] : ['Employee'];
  }

  private toApiMessageTypes(values: string[]): string[] {
    const list = Array.isArray(values) ? values : [values as unknown as string];
    const whitelist = new Set(['Text', 'Image', 'File', 'Voice', 'Video']);
    return list
      .map(item => String(item ?? '').trim())
      .filter(Boolean)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => whitelist.has(item));
  }

  private toApiSenderTypes(values: string[]): string[] {
    const list = Array.isArray(values) ? values : [values as unknown as string];
    const whitelist = new Set(['Employee', 'Customer', 'Member']);
    return list
      .map(item => String(item ?? '').trim())
      .filter(Boolean)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => whitelist.has(item));
  }

  private fromApiMessageTypes(values?: string[]): string[] {
    if (!Array.isArray(values)) return [];
    const whitelist = new Set(['TEXT', 'IMAGE', 'FILE', 'VOICE', 'VIDEO']);
    return values
      .map(item =>
        String(item ?? '')
          .trim()
          .toUpperCase()
      )
      .filter(item => whitelist.has(item))
      .map(item => item.charAt(0) + item.slice(1).toLowerCase());
  }

  private fromApiSenderTypes(values?: string[]): string[] {
    if (!Array.isArray(values)) return [];
    const whitelist = new Set(['EMPLOYEE', 'CUSTOMER', 'MEMBER']);
    return values
      .map(item =>
        String(item ?? '')
          .trim()
          .toUpperCase()
      )
      .filter(item => whitelist.has(item))
      .map(item => item.charAt(0) + item.slice(1).toLowerCase());
  }

  private resolveReplyReceiverTypes(detail: AssistantDetailData | AssistantStoreApp): string[] {
    const candidates: unknown[] = [
      (detail as any).replyReceiverTypes,
      (detail as any).replyVisibleFor,
      (detail as any).replyVisibleScope,
      (detail as any).replyVisibleTargets
    ];

    for (const candidate of candidates) {
      const parsed = this.parseReplyReceiverTypesCandidate(candidate);
      if (parsed) return parsed;
    }

    return [];
  }

  private getReplyReceiverTypesForDisplay(detail: AssistantDetailData | AssistantStoreApp): string[] {
    const receiverTypes = this.resolveReplyReceiverTypes(detail);
    return receiverTypes.includes('Customer') ? ['Employee', 'Customer'] : ['Employee'];
  }

  private parseReplyReceiverTypesCandidate(candidate: unknown): string[] | null {
    if (Array.isArray(candidate)) {
      const parsed = this.fromApiSenderTypes(candidate as string[]).filter(item => item === 'Customer');
      const unique = Array.from(new Set(parsed));
      if (unique.length > 0) return ['Customer'];
      return [];
    }

    const normalized = String(candidate ?? '')
      .trim()
      .toLowerCase();
    if (!normalized) return null;
    if (normalized === 'customer') return ['Customer'];
    if (normalized === 'both' || normalized === 'all' || normalized === 'customer_employee' || normalized === 'employee_customer') {
      return ['Customer'];
    }
    if (normalized === 'employee') return [];
    return null;
  }

  private splitKeywords(value: unknown): string[] {
    const text = String(value ?? '').trim();
    if (!text) return [];
    return text
      .split(/[,\n;\uff0c\uff1b]+/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  private parseTrialUsed(value: AssistantListData['trialUsed']): boolean {
    return value === true;
  }

  private parseTrialFlag(value: unknown): boolean {
    return value === true;
  }

  private resolveDeductPoints(candidates: Array<number | string | null | undefined>): number {
    for (const value of candidates) {
      const parsed = Number(value ?? 0);
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.floor(parsed);
      }
    }
    return 0;
  }

  private resolveExplicitDeductPoint(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return Math.floor(parsed);
  }

  private resolveAvatarUrl(raw: Partial<AssistantListItem> | null | undefined): string | undefined {
    if (!raw) return undefined;
    const normalized = String((raw as any).avatarUrl ?? '').trim();
    return normalized || undefined;
  }

  private getPlanSelectedPricing(plan?: PricingPlan | null): SelectedPricing | null {
    const pricingConfigId = String(plan?.pricingConfigId ?? '').trim();
    const pricingItemId = String(plan?.pricingItemId ?? plan?.id ?? '').trim();
    const pricingCode = String(plan?.pricingCode ?? '').trim();
    if (!pricingItemId || !pricingCode) {
      return null;
    }
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode
    };
  }

  private getAssistantSelectedPricing(
    item?: Partial<AssistantItem | AssistantListItem | AssistantDetailData> | null
  ): SelectedPricing | null {
    const raw = item as any;
    const prefix = raw?.needPay === true ? 'latest' : 'current';
    const prefixPricingConfigId = String(raw?.[`${prefix}PricingConfigId`] ?? '').trim();
    const prefixPricingItemId = String(raw?.[`${prefix}PricingItemId`] ?? '').trim();
    const prefixPricingCode = String(raw?.[`${prefix}PricingCode`] ?? '').trim();
    if (prefixPricingItemId || prefixPricingCode) {
      return {
        pricingConfigId: prefixPricingConfigId,
        pricingItemId: prefixPricingItemId,
        pricingCode: prefixPricingCode
      };
    }
    const source = raw?.lastOrder ?? item;
    const pricingConfigId = String(source?.pricingConfigId ?? '').trim();
    const pricingItemId = String(source?.pricingItemId ?? '').trim();
    const pricingCode = String(source?.pricingCode ?? '').trim();
    if (!pricingItemId && !pricingCode) {
      return null;
    }
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode
    };
  }

  private resolvePreferredPricingItemId(preferredPricing?: Partial<SelectedPricing> | null): string | null {
    const pricingItemId = String(preferredPricing?.pricingItemId ?? '').trim();
    if (pricingItemId) {
      return pricingItemId;
    }
    const pricingCode = String(preferredPricing?.pricingCode ?? '').trim();
    if (!pricingCode) {
      return null;
    }
    const matchedPlans = this.pricingPlans().filter(plan => String(plan.pricingCode ?? '').trim() === pricingCode);
    if (matchedPlans.length === 1) {
      return matchedPlans[0].id;
    }
    return null;
  }

  private loadAssistantPackages(): void {
    this.packagesLoading = true;
    this.http
      .post(ConstantUtil.ADMIN_PRICING_PACKAGE_ITEM_V1_LIST, { pricingCode: ASSISTANT_PRICING_CODE })
      .pipe(
        finalize(() => {
          this.packagesLoading = false;
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0;
          const rawList = Array.isArray(res?.data) ? (res.data as AssistantPackageItem[]) : [];
          if (!isSuccess || rawList.length === 0) {
            this.pricingPlans.set(this.getFallbackPricingPlans());
            this.ensureSelectedPricing(this.pendingPreferredPricing);
            this.pendingPreferredPricing = null;
            return;
          }

          const mapped = rawList
            .map((item, index) => this.mapPackageToPlan(item, index))
            .filter((item): item is PricingPlan => !!item)
            .sort((a, b) => this.comparePlans(a, b));
          const filtered = this.trialUsed ? mapped.filter(item => !this.isTrialPlan(item)) : mapped;

          if (filtered.length === 0) {
            this.pricingPlans.set(this.getFallbackPricingPlans());
          } else {
            this.pricingPlans.set(filtered);
          }
          this.ensureSelectedPricing(this.pendingPreferredPricing);
          if (this.pendingDeductionInputs) {
            this.deductionInputs = { ...this.pendingDeductionInputs };
          }
          this.pendingPreferredPricing = null;
        },
        error: () => {
          this.pricingPlans.set(this.getFallbackPricingPlans());
          this.ensureSelectedPricing(this.pendingPreferredPricing);
          if (this.pendingDeductionInputs) {
            this.deductionInputs = { ...this.pendingDeductionInputs };
          }
          this.pendingPreferredPricing = null;
        }
      });
  }

  private mapPackageToPlan(item: AssistantPackageItem, index: number): PricingPlan | null {
    const id = String(item.id ?? '').trim();
    if (!id) return null;

    const trial = item.trialEnabled === true || ((Number(item.trialDays ?? 0) || 0) > 0 && Number(item.priceCny ?? 0) <= 0);
    const durationMonths = Number(item.durationMonths ?? 0) || undefined;
    const resolvedPrice = this.resolvePlanPrice(item);
    const name = this.resolvePlanName(item, trial, durationMonths);
    return {
      id,
      name,
      price: trial ? 0 : resolvedPrice,
      trial,
      trialDays: Number(item.trialDays ?? 0) || undefined,
      recommended: false,
      order: index,
      durationMonths,
      pricingConfigId: String(item.pricingConfigId ?? '').trim() || undefined,
      pricingItemId: id,
      pricingCode: String(item.pricingCode ?? ASSISTANT_PRICING_CODE).trim() || ASSISTANT_PRICING_CODE
    };
  }

  private getFallbackPricingPlans(): PricingPlan[] {
    return [
      { id: 'monthly', name: this.t('assistant.management.create.pay.fallback.monthly'), price: 99, recommended: true, durationMonths: 1 },
      { id: 'bimonthly', name: this.t('assistant.management.create.pay.fallback.bimonthly'), price: 179, durationMonths: 2 },
      { id: 'quarterly', name: this.t('assistant.management.create.pay.fallback.quarterly'), price: 249, durationMonths: 3 },
      { id: 'halfyear', name: this.t('assistant.management.create.pay.fallback.halfyear'), price: 449, durationMonths: 6 },
      { id: 'yearly', name: this.t('assistant.management.create.pay.fallback.yearly'), price: 799, durationMonths: 12 }
    ];
  }

  private getPlanDurationText(plan: PricingPlan): string {
    const months = Number(plan.durationMonths ?? 0);
    if (!Number.isFinite(months) || months <= 0) {
      return this.t('assistant.management.create.pay.plan');
    }
    return `${months} ${this.t('pricing.package.item.duration.months')}`;
  }

  private resolvePlanPrice(item: AssistantPackageItem): number {
    const discountPrice = Number(item.discountPriceCny ?? NaN);
    if (Number.isFinite(discountPrice) && discountPrice >= 0) {
      return discountPrice;
    }
    const price = Number(item.priceCny ?? NaN);
    return Number.isFinite(price) && price >= 0 ? price : 0;
  }

  private resolvePlanName(item: AssistantPackageItem, trial: boolean, durationMonths?: number): string {
    if (trial) {
      return String(item.description ?? '').trim() || this.t('assistant.management.create.pay.trial.name');
    }

    const durationKeyMap: Record<number, string> = {
      1: 'assistant.management.create.pay.fallback.monthly',
      2: 'assistant.management.create.pay.fallback.bimonthly',
      3: 'assistant.management.create.pay.fallback.quarterly',
      6: 'assistant.management.create.pay.fallback.halfyear',
      12: 'assistant.management.create.pay.fallback.yearly'
    };
    const key = durationMonths ? durationKeyMap[durationMonths] : '';
    if (key) {
      return this.t(key);
    }
    return String(item.description ?? '').trim() || this.getPlanDurationText({ id: '', name: '', price: 0, durationMonths });
  }

  private comparePlans(a: PricingPlan, b: PricingPlan): number {
    if (this.isTrialPlan(a) !== this.isTrialPlan(b)) {
      return this.isTrialPlan(a) ? -1 : 1;
    }
    const durationDelta = Number(a.durationMonths ?? Number.MAX_SAFE_INTEGER) - Number(b.durationMonths ?? Number.MAX_SAFE_INTEGER);
    if (durationDelta !== 0) {
      return durationDelta;
    }
    return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
  }

  private ensureSelectedPricing(preferredPricing?: Partial<SelectedPricing> | null): void {
    const plans = this.pricingPlans();
    if (plans.length === 0) {
      this.selectedPricingItemId = '';
      return;
    }
    const preferred = this.resolvePreferredPricingItemId(preferredPricing);
    if (preferred && plans.some(item => item.id === preferred)) {
      this.selectedPricingItemId = preferred;
      this.deductionInputs = { uupon: null };
      return;
    }
    const exists = plans.some(item => item.id === this.selectedPricingItemId);
    if (!exists) {
      const recommend = plans.find(item => item.recommended);
      this.selectedPricingItemId = recommend?.id ?? plans[0].id;
      this.deductionInputs = { uupon: null };
    }
  }

  private resetAvatarPreview(): void {
    this.revokeAvatarObjectUrl();
    this.avatarPreview = '';
  }

  private revokeAvatarObjectUrl(): void {
    if (!this.avatarObjectUrl) return;
    URL.revokeObjectURL(this.avatarObjectUrl);
    this.avatarObjectUrl = null;
  }

  private getRemainingCashFor(type: BalanceType): number {
    return Math.max(0, this.getSelectedPlanPrice());
  }

  private syncDeductionLock(type: BalanceType): void {
    return;
  }

  private buildBalanceCards(raw?: any): BalanceCard[] {
    return [{ type: 'uupon', points: this.toNonNegativeInteger(raw?.uuponPoint), deductRate: 4 }];
  }

  private normalizeUUPonMultiple(points: number): number | null {
    const normalized = Math.floor(Number(points) || 0);
    const multiple = normalized - (normalized % 4);
    return multiple > 0 ? multiple : null;
  }

  private validateUUPonMultiple(): boolean {
    const points = this.getDeductPoints('uupon');
    if (points > 0 && points % 4 !== 0) {
      this.message.warning(this.t('assistant.management.create.pay.uupon-multiple-of-four'));
      return false;
    }
    return true;
  }

  private resolveAipoolTenantId(raw?: any): string {
    return String(raw?.aipoolTenantId ?? '').trim();
  }

  private payAssistantOrder(orderId: string, assistantId?: string, renewal?: AssistantStoreRenewalContext | null): void {
    const apiPath = ConstantUtil.ADMIN_ASSISTANT_ORDER_V1_PAY.replace('{orderId}', encodeURIComponent(orderId));
    if (assistantId) {
      this.payingAssistantId.set(assistantId);
    } else {
      this.setSubmitLoading(true);
    }
    this.http
      .post(apiPath, this.buildPayPayload(orderId, assistantId))
      .pipe(
        finalize(() => {
          if (assistantId) {
            this.payingAssistantId.set(null);
          } else {
            this.setSubmitLoading(false);
          }
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            showFallbackHttpError(this.message, res?.msg, this.t('common.load-failed'));
            return;
          }
          const payload = res?.data ?? res;
          this.storePaymentSnapshot('assistant', orderId, assistantId, payload);
          if (this.openPaymentUrl(payload, orderId, renewal)) return;
          if (this.isPaymentPaid(payload)) {
            this.completePaidAssistantStorePurchase(renewal);
            this.completePaidAssistantWithoutPaymentUrl();
          }
        },
        error: () => {
          this.message.error(this.t('common.load-failed'));
        }
      });
  }

  private handleAssistantStorePurchaseOrder(order?: any, renewal?: AssistantStoreRenewalContext | null): void {
    const orderId = this.getAssistantOrderId(order);
    if (orderId) {
      const bizId = String(order?.bizId ?? order?.assistantId ?? '').trim() || undefined;
      this.payAssistantOrder(orderId, bizId, renewal);
      return;
    }
    if (this.openPaymentUrl(order, orderId ?? undefined, renewal)) {
      return;
    }
    if (this.isPaymentPaid(order)) {
      this.completePaidAssistantStorePurchase(renewal);
      return;
    }
    this.closeAssistantStorePackageModal();
    this.message.success(this.t('assistant.management.store.message.renewal-created'));
    this.loadAssistantStoreInstances();
  }

  private buildPayPayload(orderId?: string, assistantId?: string): any {
    const urls = this.buildPaymentReturnUrls('assistant', orderId, assistantId);
    const payload: any = {
      tenantId: this.getTenantId(),
      resultURL: urls.resultURL,
      cancelURL: urls.cancelURL
    };
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    return payload;
  }

  private buildPaymentReturnUrls(
    orderType: PaymentOrderType,
    orderId?: string,
    assistantId?: string
  ): { resultURL: string; cancelURL: string } {
    return {
      resultURL: this.buildPaymentReturnUrl(orderType, 'success', orderId, assistantId),
      cancelURL: this.buildPaymentReturnUrl(orderType, 'failed', orderId, assistantId)
    };
  }

  private buildPaymentReturnUrl(
    orderType: PaymentOrderType,
    paymentResult: 'success' | 'failed',
    orderId?: string,
    assistantId?: string
  ): string {
    return buildPaymentResultUrl({
      orderType,
      paymentResult,
      tenantId: this.getTenantId(),
      aipoolTenantId: this.aipoolTenantId,
      orderId,
      targetId: assistantId,
      sourcePath: this.getSourcePath()
    });
  }

  private openPaymentUrl(raw?: any, fallbackOrderId?: string, renewal?: AssistantStoreRenewalContext | null): boolean {
    const payUrl = raw?.paymentUrl ?? raw?.payUrl ?? null;
    if (payUrl) {
      this.openPaymentModal(
        String(payUrl),
        String(raw?.orderId ?? raw?.latestOrderId ?? fallbackOrderId ?? this.payingOrderId ?? '').trim() || undefined,
        renewal
      );
      return true;
    }
    return false;
  }

  private openPaymentModal(
    paymentUrl: string,
    orderId?: string,
    renewal: AssistantStoreRenewalContext | null = this.captureSelectedStoreRenewal()
  ): void {
    const paymentModal = this.modal.create({
      nzContent: PaymentIframeModalComponent,
      nzWidth: '1200px',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'payment-iframe-modal',
      nzData: {
        paymentUrl,
        orderId,
        orderType: 'assistant'
      }
    });

    paymentModal.afterClose.subscribe(result => {
      if (this.storePackageModalVisible()) {
        this.closeAssistantStorePackageModal();
      }
      this.completePaidAssistantWithoutPaymentUrl();
      if (
        result?.refresh === true &&
        String(result?.paymentStatus ?? '')
          .trim()
          .toLowerCase() === 'success' &&
        renewal
      ) {
        this.refreshAssistantStoreInstancesAfterRenewal(renewal.assistantId, renewal.previous);
        return;
      }
      this.loadAssistantStoreInstances();
    });
  }

  private captureSelectedStoreRenewal(): AssistantStoreRenewalContext | null {
    const assistantId = String(this.selectedStorePurchaseBizId() ?? '').trim();
    if (!assistantId) {
      return null;
    }
    const previous = this.storeSubscriptions().find(subscription => subscription.assistantId === assistantId);
    return previous ? { assistantId, previous } : null;
  }

  private completePaidAssistantStorePurchase(renewal?: AssistantStoreRenewalContext | null): void {
    this.closeAssistantStorePackageModal();
    if (renewal) {
      this.refreshAssistantStoreInstancesAfterRenewal(renewal.assistantId, renewal.previous);
      return;
    }
    this.loadAssistantStoreInstances();
  }

  private refreshAssistantStoreInstancesAfterRenewal(assistantId: string, previous: AssistantStoreSubscription, attempt = 1): void {
    this.stopStoreRenewalRefresh();
    this.loadAssistantStoreInstances(subscriptions => {
      const renewed = subscriptions.find(subscription => subscription.assistantId === assistantId);
      if (this.hasStoreRenewalApplied(previous, renewed) || attempt >= STORE_RENEWAL_REFRESH_MAX_ATTEMPTS) {
        return;
      }
      this.storeRenewalRefreshTimer = window.setTimeout(() => {
        this.storeRenewalRefreshTimer = null;
        this.refreshAssistantStoreInstancesAfterRenewal(assistantId, previous, attempt + 1);
      }, STORE_RENEWAL_REFRESH_RETRY_DELAY_MS);
    });
  }

  private hasStoreRenewalApplied(previous: AssistantStoreSubscription, renewed?: AssistantStoreSubscription): boolean {
    if (!renewed) {
      return false;
    }
    return Number(renewed.validUntil ?? 0) > Number(previous.validUntil ?? 0);
  }

  private stopStoreRenewalRefresh(): void {
    if (this.storeRenewalRefreshTimer !== null) {
      window.clearTimeout(this.storeRenewalRefreshTimer);
      this.storeRenewalRefreshTimer = null;
    }
  }

  private completePaidAssistantWithoutPaymentUrl(): void {
    this.createModalVisible = false;
    this.wizardMode = 'create';
    this.editingAssistantId = null;
    this.payingOrderId = null;
    this.avatarFile = null;
    this.avatarChanged = false;
    this.editingAvatarUrl = '';
    this.reloadAssistantList(true);
  }

  private isPaymentPaid(raw?: any): boolean {
    const statusValues = [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    if (statusValues.some(status => ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status))) {
      return true;
    }
    if (Number(raw?.status) === 30 || Number(raw?.latestOrderStatus ?? raw?.orderStatus) === 30 || Number(raw?.paymentStatus) === 20) {
      return true;
    }
    return raw?.needPay === false || raw?.payRequired === false;
  }

  private getSourcePath(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  private storePaymentSnapshot(orderType: PaymentOrderType, orderId: string, assistantId: string | undefined, raw?: any): void {
    storePaymentSessionSnapshot({
      orderType,
      orderId,
      targetId: String(assistantId ?? raw?.assistantId ?? raw?.targetId ?? '').trim() || undefined,
      tenantId: this.getTenantId(),
      aipoolTenantId: String(this.aipoolTenantId ?? raw?.aipoolTenantId ?? '').trim() || undefined,
      paymentUrl: String(raw?.paymentUrl ?? '').trim() || undefined,
      reserveTimeoutSeconds: raw?.reserveTimeoutSeconds,
      payStartTime: raw?.payStartTime,
      payExpireTime: raw?.payExpireTime,
      orderStatus: raw?.orderStatus,
      paymentStatus: raw?.paymentStatus,
      payRequired: raw?.payRequired
    });
  }

  private getAssistantOrderId(raw?: any): string | null {
    const value = String(raw?.latestOrderId ?? raw?.orderId ?? '').trim();
    return value || null;
  }

  private toNonNegativeInteger(value: unknown): number {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.floor(parsed);
  }

  private validateCurrentStep(): boolean {
    const controlsByStep: CreateFormControl[][] = [
      ['avatar', 'name', 'description'],
      ['responseMode', 'webhookUrl'],
      ['messageTypes', 'senderTypes', 'replyReceiverTypes'],
      []
    ];
    const controls = controlsByStep[this.createStep] ?? [];
    let valid = true;
    controls.forEach(key => {
      const c = this.createForm.controls[key];
      c.markAsDirty();
      c.updateValueAndValidity();
      if (c.invalid) valid = false;
    });
    if (this.createStep === 1 && !this.validateBatchConfig(true)) {
      valid = false;
    }
    return valid;
  }

  private validateBatchConfig(markAsDirty: boolean): boolean {
    if (!this.isBatchMode()) {
      this.batchTriggerConditionMissing = false;
      return true;
    }
    const countControl = this.createForm.controls.batchMessageCount;
    const intervalControl = this.createForm.controls.batchIntervalMinutes;
    const keywordsControl = this.createForm.controls.batchKeywords;

    if (markAsDirty) {
      countControl.markAsDirty();
      intervalControl.markAsDirty();
      keywordsControl.markAsDirty();
    }

    countControl.updateValueAndValidity();
    intervalControl.updateValueAndValidity();

    const hasCount = this.hasPositiveValue(countControl.value);
    const hasInterval = this.hasPositiveValue(intervalControl.value);
    const hasKeywords = String(keywordsControl.value ?? '').trim().length > 0;
    const hasAnyTriggerCondition = hasCount || hasInterval || hasKeywords;
    this.batchTriggerConditionMissing = !hasAnyTriggerCondition;

    return countControl.valid && intervalControl.valid && hasAnyTriggerCondition;
  }

  private hasPositiveValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 1;
  }

  private optionalMin(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') return null;
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric < min) {
        return { min: { min, actual: value } };
      }
      return null;
    };
  }

  private minArrayLength(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value as unknown[];
      if (Array.isArray(val) && val.length >= min) return null;
      return { minArrayLength: true };
    };
  }
}
